import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  TableSortLabel,
  Pagination,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  GetApp,
  PictureAsPdf,
  TableChart,
  FilterList,
  ExpandMore,
  ExpandLess,
  AccessTime,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/authService';
import LocationViewer from '../common/LocationViewer';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatStringWithNumbers, formatDate, formatDateTime, formatCurrency, formatNumber } from '../../utils/i18nHelpers';
import { loadArabicFont, setArabicFont, containsArabic } from '../../utils/pdfArabicFont';

// Safari shows today's date by default for type="date" inputs even when empty.
// This wrapper renders as type="text" when empty and switches to type="date" on focus.
const DateField: React.FC<React.ComponentProps<typeof TextField>> = (props) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const justActivated = React.useRef(false);
  const hasValue = Boolean(props.value);
  const showDate = active || hasValue;
  return (
    <TextField
      {...props}
      type={showDate ? 'date' : 'text'}
      inputRef={inputRef}
      placeholder={showDate ? undefined : (props.placeholder || 'mm/dd/yyyy')}
      onClick={() => {
        if (!showDate) {
          justActivated.current = true;
          setActive(true);
          requestAnimationFrame(() => {
            const el = inputRef.current;
            if (el) {
              // Clear any Safari auto-fill before showing picker
              el.value = '';
              el.focus();
              try { el.showPicker?.(); } catch (_) { /* ignore */ }
            }
            // Allow onChange after a short delay (picker interaction)
            setTimeout(() => { justActivated.current = false; }, 300);
          });
        } else {
          try { inputRef.current?.showPicker?.(); } catch (_) { /* ignore */ }
        }
      }}
      onChange={(e) => {
        // Ignore Safari auto-fill that fires immediately on type switch
        if (justActivated.current && !hasValue) return;
        props.onChange?.(e);
      }}
      onBlur={(e) => {
        justActivated.current = false;
        if (!props.value) setActive(false);
        props.onBlur?.(e);
      }}
      InputLabelProps={{ shrink: true }}
      autoComplete="off"
    />
  );
};

interface Building {
  id: number;
  name: string;
  name_ar?: string;
}

interface Flat {
  id: number;
  flat_number: string;
  building_id: number;
  building?: Building;
}

interface Technician {
  id: number;
  service_type: string;
  name?: string;
  name_ar?: string;
  user?: {
    id: number;
    name: string;
    name_ar?: string;
    email: string;
  } | null;
}

interface User {
  id: number;
  name: string;
  name_ar?: string;
  email?: string;
}

interface CheckInRecord {
  id: number;
  check_in_time?: string;
  check_out_time?: string;
}

interface PauseRecord {
  id: number;
  action: 'pause' | 'resume';
  created_at?: string;
}

interface OutOfSiteTaskReportRow {
  id: number;
  technician_id: number | null;
  objective: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  start_time: string;
  expected_end_time?: string | null;
  end_time?: string | null;
  notes?: string | null;
  technician?: {
    id: number;
    user?: {
      id: number;
      name: string;
      name_ar?: string;
    } | null;
  } | null;
}

interface WorkOrder {
  id: number;
  work_order_number: string;
  title: string;
  title_ar?: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'paused';
  scheduled_from?: string;
  scheduled_to?: string;
  completed_date?: string;
  created_at: string;
  flat?: Flat;
  technician?: Technician;
  created_by?: User;
  assigned_user?: User;
  check_ins?: CheckInRecord[];
  pauses?: PauseRecord[];
}

const getLastPauseTime = (wo: WorkOrder): string | null => {
  const records = (wo.pauses || []).filter(p => p.action === 'pause' && p.created_at);
  if (records.length === 0) return null;
  return records[records.length - 1].created_at as string;
};

const getLastResumeTime = (wo: WorkOrder): string | null => {
  const records = (wo.pauses || []).filter(p => p.action === 'resume' && p.created_at);
  if (records.length === 0) return null;
  return records[records.length - 1].created_at as string;
};

type SortDirection = 'asc' | 'desc';
type WOSortField = 'work_order_number' | 'title' | 'building' | 'flat' | 'provider' | 'status' | 'priority' | 'created_by' | 'created_at' | 'scheduled_from' | 'scheduled_to' | 'completed_date' | 'check_in_time' | 'check_out_time' | 'wo_duration' | 'pause_count';
type ExpenseSortField = 'expense_number' | 'title' | 'work_order_number' | 'service_type' | 'building' | 'vendor' | 'amount' | 'expense_date';
type CheckInSortField = 'technician' | 'building' | 'check_in_time' | 'check_out_time' | 'duration' | 'status';

const Reports: React.FC = () => {
  const { can } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [reportData, setReportData] = useState<WorkOrder[]>([]);
  const [expenseReportData, setExpenseReportData] = useState<any[]>([]);
  const [checkInReportData, setCheckInReportData] = useState<any[]>([]);
  const [outOfSiteTasks, setOutOfSiteTasks] = useState<OutOfSiteTaskReportRow[]>([]);
  const [outOfSiteLoading, setOutOfSiteLoading] = useState(false);
  const [outOfSitePage, setOutOfSitePage] = useState(1);
  const [outOfSiteTotalPages, setOutOfSiteTotalPages] = useState(1);
  const [outOfSiteTotalCount, setOutOfSiteTotalCount] = useState(0);

  // Daily Activity Report
  const [dailyActivityData, setDailyActivityData] = useState<any[]>([]);
  const [dailyActivityLoading, setDailyActivityLoading] = useState(false);
  const [dailyActivityDate, setDailyActivityDate] = useState(new Date().toISOString().slice(0, 10));
  const [dailyActivityTechId, setDailyActivityTechId] = useState('');

  // Attendance Report
  const [attendanceData, setAttendanceData] = useState<any[]>([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendancePeriod, setAttendancePeriod] = useState<'daily' | 'weekly' | 'monthly' | 'custom'>('daily');
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().slice(0, 10));
  const [attendanceDateFrom, setAttendanceDateFrom] = useState('');
  const [attendanceDateTo, setAttendanceDateTo] = useState('');
  const [attendanceTechId, setAttendanceTechId] = useState('');
  const [attendanceServiceType, setAttendanceServiceType] = useState('');

  // Sort state for Work Order Reports
  const [woSortField, setWoSortField] = useState<WOSortField>('created_at');
  const [woSortDir, setWoSortDir] = useState<SortDirection>('desc');
  // Sort state for Expense Reports
  const [expSortField, setExpSortField] = useState<ExpenseSortField>('expense_date');
  const [expSortDir, setExpSortDir] = useState<SortDirection>('desc');
  // Sort state for Check-In Reports
  const [ciSortField, setCiSortField] = useState<CheckInSortField>('check_in_time');
  const [ciSortDir, setCiSortDir] = useState<SortDirection>('desc');
  // Sort state for Daily Activity
  type DASortField = 'time' | 'type' | 'technician' | 'reference' | 'title' | 'building' | 'status' | 'start_time' | 'end_time' | 'duration';
  const [daSortField, setDaSortField] = useState<DASortField>('time');
  const [daSortDir, setDaSortDir] = useState<SortDirection>('asc');

  // Work Orders Filter states
  const [filters, setFilters] = useState({
    technician_id: '',
    status: [] as string[],
    priority: [] as string[],
    service_type: '',
    created_by: '',
    building_id: '',
    flat_id: '',
    date_from: '',
    date_to: '',
    scheduled_from: '',
    scheduled_to: '',
    completed_from: '',
    completed_to: '',
    activity_types: [] as string[],
  });

  // Expenses Filter states
  const [expenseFilters, setExpenseFilters] = useState({
    work_order_id: '',
    service_type: '',
    building_id: '',
    flat_id: '',
    vendor: '',
    date_from: '',
    date_to: '',
    amount_min: '',
    amount_max: '',
  });

  // Dropdown data
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [flats, setFlats] = useState<Flat[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [serviceTypes, setServiceTypes] = useState<string[]>([]);
  
  // Expense filter dropdown data
  const [expenseBuildings, setExpenseBuildings] = useState<Building[]>([]);
  const [expenseFlats, setExpenseFlats] = useState<Flat[]>([]);
  const [expenseServiceTypes, setExpenseServiceTypes] = useState<string[]>([]);
  const [serviceTypesObjects, setServiceTypesObjects] = useState<Array<{ id: number; name_en: string; name_ar: string }>>([]);
  const [vendors, setVendors] = useState<string[]>([]);
  const [workOrders, setWorkOrders] = useState<any[]>([]);

  // Check-in report state
  const [checkInFilters, setCheckInFilters] = useState({
    user_id: '',
    building_id: '',
    status: '',
    date_from: '',
    date_to: '',
  });
  const [checkInBuildings, setCheckInBuildings] = useState<Building[]>([]);
  const [checkInTechnicians, setCheckInTechnicians] = useState<User[]>([]);

  // Out-of-Site Tasks report filters
  const [outOfSiteFilters, setOutOfSiteFilters] = useState({
    technician_id: '',
    status: 'all',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const filterOptionsRes = await api.get('/reports/filter-options').catch(() => ({ data: {} }));
      
      if (filterOptionsRes.data) {
        setBuildings(Array.isArray(filterOptionsRes.data.buildings) ? filterOptionsRes.data.buildings : []);
        setTechnicians(Array.isArray(filterOptionsRes.data.technicians) ? filterOptionsRes.data.technicians : []);
        setUsers(Array.isArray(filterOptionsRes.data.creators) ? filterOptionsRes.data.creators : []);
        setServiceTypes(Array.isArray(filterOptionsRes.data.service_types) ? filterOptionsRes.data.service_types : []);
      }
      
      // Fetch expense filter options
      try {
        const expenseFilterRes = await api.get('/reports/expense-filter-options').catch(() => ({ data: {} }));
        if (expenseFilterRes.data) {
          setExpenseBuildings(Array.isArray(expenseFilterRes.data.buildings) ? expenseFilterRes.data.buildings : []);
          setExpenseServiceTypes(Array.isArray(expenseFilterRes.data.service_types) ? expenseFilterRes.data.service_types : []);
          setVendors(Array.isArray(expenseFilterRes.data.vendors) ? expenseFilterRes.data.vendors : []);
          setWorkOrders(Array.isArray(expenseFilterRes.data.work_orders) ? expenseFilterRes.data.work_orders : []);
        }
      } catch (error) {
        console.error('Error fetching expense filter options:', error);
      }

      // Fetch check-in filter options
      try {
        const checkInFilterRes = await api.get('/reports/check-in-filter-options').catch(() => ({ data: {} }));
        if (checkInFilterRes.data) {
          setCheckInBuildings(Array.isArray(checkInFilterRes.data.buildings) ? checkInFilterRes.data.buildings : []);
          setCheckInTechnicians(Array.isArray(checkInFilterRes.data.technicians) ? checkInFilterRes.data.technicians : []);
        }
      } catch (error) {
        console.error('Error fetching check-in filter options:', error);
      }

      // Fetch service types with name_en and name_ar
      try {
        const serviceTypesRes = await api.get('/service-types').catch(() => ({ data: [] }));
        setServiceTypesObjects(Array.isArray(serviceTypesRes.data) ? serviceTypesRes.data : []);
      } catch (error) {
        console.error('Error fetching service types:', error);
        setServiceTypesObjects([]);
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchOutOfSiteTasks = async (page: number = 1) => {
    try {
      setOutOfSiteLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('per_page', '10');
      if (outOfSiteFilters.technician_id) {
        params.append('technician_id', outOfSiteFilters.technician_id);
      }
      if (outOfSiteFilters.status !== 'all') {
        params.append('status', outOfSiteFilters.status);
      }
      if (outOfSiteFilters.date_from) {
        params.append('date_from', outOfSiteFilters.date_from);
      }
      if (outOfSiteFilters.date_to) {
        params.append('date_to', outOfSiteFilters.date_to);
      }
      const res = await api.get(`/out-of-site-tasks?${params.toString()}`);
      const data = res.data;
      const rows = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      setOutOfSiteTasks(rows);
      if (data?.last_page) {
        setOutOfSiteTotalPages(data.last_page);
        setOutOfSiteTotalCount(data.total || rows.length);
        setOutOfSitePage(data.current_page || page);
      } else {
        setOutOfSiteTotalPages(1);
        setOutOfSiteTotalCount(rows.length);
        setOutOfSitePage(page);
      }
    } catch (error: any) {
      console.error('Error fetching Out-of-Site Tasks report:', error);
      notifyError(error?.response?.data?.error || 'Failed to load Out-of-Site Tasks report');
    } finally {
      setOutOfSiteLoading(false);
    }
  };

  const generateDailyActivity = async () => {
    try {
      setDailyActivityLoading(true);
      const params = new URLSearchParams();
      if (dailyActivityDate) params.append('date', dailyActivityDate);
      if (dailyActivityTechId) params.append('technician_id', dailyActivityTechId);
      const response = await api.get(`/reports/daily-activity?${params.toString()}`);
      setDailyActivityData(Array.isArray(response.data) ? response.data : []);
      notifySuccess(t('reports.messages.generate.success').replace('{{count}}', String(response.data.length || 0)));
    } catch (error: any) {
      console.error('Error fetching daily activity:', error);
      notifyError(error?.response?.data?.error || 'Failed to generate daily activity report');
    } finally {
      setDailyActivityLoading(false);
    }
  };

  // Resolve the attendance report date range from the selected period.
  const getAttendanceRange = (): { from: string; to: string } => {
    if (attendancePeriod === 'custom') {
      return { from: attendanceDateFrom, to: attendanceDateTo };
    }
    const base = attendanceDate ? new Date(attendanceDate + 'T00:00:00') : new Date();
    const toStr = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    if (attendancePeriod === 'daily') {
      return { from: toStr(base), to: toStr(base) };
    }
    if (attendancePeriod === 'weekly') {
      // Week starting Monday containing the selected date.
      const day = base.getDay(); // 0 = Sun
      const diffToMonday = (day + 6) % 7;
      const start = new Date(base); start.setDate(base.getDate() - diffToMonday);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return { from: toStr(start), to: toStr(end) };
    }
    // monthly
    const start = new Date(base.getFullYear(), base.getMonth(), 1);
    const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    return { from: toStr(start), to: toStr(end) };
  };

  const generateAttendanceReport = async () => {
    try {
      setAttendanceLoading(true);
      const { from, to } = getAttendanceRange();
      const params = new URLSearchParams();
      if (from) params.append('date_from', from);
      if (to) params.append('date_to', to);
      if (attendanceTechId) params.append('technician_id', attendanceTechId);
      params.append('per_page', '1000');
      const response = await api.get(`/attendance?${params.toString()}`);
      let rows: any[] = Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []);
      // Filter by technician type (service_type) on the client — backend filters by id only.
      if (attendanceServiceType) {
        rows = rows.filter((r) => (r.technician?.service_type || '') === attendanceServiceType);
      }
      // Sort by date then technician for a stable, date-wise report.
      rows.sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        const an = a.technician?.user?.name || '';
        const bn = b.technician?.user?.name || '';
        return an.localeCompare(bn);
      });
      setAttendanceData(rows);
      notifySuccess(t('reports.messages.generate.success').replace('{{count}}', String(rows.length)));
    } catch (error: any) {
      console.error('Error fetching attendance report:', error);
      notifyError(error?.response?.data?.error || 'Failed to generate attendance report');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const attTechName = (rec: any) => {
    const tech = rec?.technician;
    if (!tech) return 'N/A';
    return (language === 'ar' && tech.user?.name_ar ? tech.user.name_ar : tech.user?.name) || tech.name || 'N/A';
  };

  const attDuration = (mins: number | null | undefined) => {
    if (mins == null) return '-';
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  const attCoords = (lat: number | null, lng: number | null) =>
    (lat != null && lng != null) ? `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}` : 'N/A';

  const exportAttendanceToExcel = () => {
    if (attendanceData.length === 0) { notifyError(t('reports.messages.export.noData')); return; }
    import('xlsx' as any).then((XLSX: any) => {
      const locale = language === 'ar' ? 'ar-SA' : 'en-US';
      const asTime = (v: string | null) => v ? new Date(v).toLocaleString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
      const rows = attendanceData.map((r) => ({
        [t('reports.attendance.table.date')]: r.date,
        [t('reports.attendance.table.technician')]: attTechName(r),
        [t('reports.attendance.table.firstCheckInTime')]: asTime(r.clock_in_time),
        [t('reports.attendance.table.firstCheckInLocation')]: attCoords(r.clock_in_lat, r.clock_in_lng),
        [t('reports.attendance.table.lastCheckOutTime')]: asTime(r.clock_out_time),
        [t('reports.attendance.table.lastCheckOutLocation')]: attCoords(r.clock_out_lat, r.clock_out_lng),
        [t('reports.attendance.table.totalHours')]: attDuration(r.working_hours_minutes),
        [t('reports.attendance.table.status')]: t(`attendance.status.${r.status}`),
      }));
      const totalMins = attendanceData.reduce((s, r) => s + (r.working_hours_minutes || 0), 0);
      rows.push({
        [t('reports.attendance.table.date')]: '',
        [t('reports.attendance.table.technician')]: '',
        [t('reports.attendance.table.firstCheckInTime')]: '',
        [t('reports.attendance.table.firstCheckInLocation')]: '',
        [t('reports.attendance.table.lastCheckOutTime')]: '',
        [t('reports.attendance.table.lastCheckOutLocation')]: t('reports.attendance.table.totalWorkingHours'),
        [t('reports.attendance.table.totalHours')]: attDuration(totalMins),
        [t('reports.attendance.table.status')]: '',
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 24 }, { wch: 20 }, { wch: 24 }, { wch: 12 }, { wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
      XLSX.writeFile(wb, `attendance-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      notifySuccess(t('reports.messages.export.excel.success'));
    }).catch(() => notifyError(t('reports.messages.export.excel.error')));
  };

  const exportAttendanceToPDF = () => {
    if (attendanceData.length === 0) { notifyError(t('reports.messages.export.noData')); return; }
    Promise.all([import('jspdf' as any), import('jspdf-autotable' as any)]).then(async ([jsPDFModule, autoTableModule]: any[]) => {
      const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF || jsPDFModule;
      const autoTable = autoTableModule.default || autoTableModule;
      const doc = new jsPDF('l', 'mm', 'a4');

      const dataContainsArabic = attendanceData.some((r) => containsArabic(attTechName(r)));
      let fontLoaded = false;
      if (language === 'ar' || dataContainsArabic) { fontLoaded = await loadArabicFont(doc); }
      const fontList = doc.getFontList();
      const hasArabicFont = fontList && fontList['Amiri'];
      const useArabicFont = (language === 'ar' || dataContainsArabic) && hasArabicFont && fontLoaded;

      doc.setFillColor(25, 118, 210);
      doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      if (useArabicFont) { doc.setFont('Amiri', 'normal'); } else { doc.setFont('helvetica', 'bold'); }
      doc.text(t('reports.tabs.attendance'), doc.internal.pageSize.getWidth() / 2, 18, { align: 'center' });
      doc.setFontSize(10);
      if (useArabicFont) { doc.setFont('Amiri', 'normal'); } else { doc.setFont('helvetica', 'normal'); }
      const generatedDate = new Date().toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      doc.text(`${t('reports.generatedOn')} ${generatedDate}`, doc.internal.pageSize.getWidth() / 2, 25, { align: 'center' });

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      if (useArabicFont) { doc.setFont('Amiri', 'normal'); } else { doc.setFont('helvetica', 'bold'); }
      doc.text(`${t('reports.totalRecords')} ${attendanceData.length}`, 10, 40);

      const locale = language === 'ar' ? 'ar-SA' : 'en-US';
      const asTime = (v: string | null) => v ? new Date(v).toLocaleString(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
      const tableData = attendanceData.map((r) => [
        r.date,
        attTechName(r),
        `${asTime(r.clock_in_time)}\n${attCoords(r.clock_in_lat, r.clock_in_lng)}`,
        `${asTime(r.clock_out_time)}\n${attCoords(r.clock_out_lat, r.clock_out_lng)}`,
        attDuration(r.working_hours_minutes),
        t(`attendance.status.${r.status}`),
      ]);
      const totalMins = attendanceData.reduce((s, r) => s + (r.working_hours_minutes || 0), 0);
      tableData.push(['', '', '', t('reports.attendance.table.totalWorkingHours'), attDuration(totalMins), '']);

      const tableStyles: any = { fontSize: 7, cellPadding: 2, overflow: 'linebreak', cellWidth: 'wrap', halign: 'center', valign: 'middle' };
      const headStyles: any = { fillColor: [25, 118, 210], textColor: [255, 255, 255], fontStyle: 'normal', halign: 'center', valign: 'middle', fontSize: 7 };
      if (useArabicFont) { doc.setFont('Amiri', 'normal'); tableStyles.font = 'Amiri'; tableStyles.fontStyle = 'normal'; headStyles.font = 'Amiri'; headStyles.fontStyle = 'normal'; }

      autoTable(doc, {
        head: [[
          t('reports.attendance.table.date'),
          t('reports.attendance.table.technician'),
          t('reports.attendance.table.firstCheckIn'),
          t('reports.attendance.table.lastCheckOut'),
          t('reports.attendance.table.totalHours'),
          t('reports.attendance.table.status'),
        ]],
        body: tableData,
        startY: 48,
        styles: tableStyles,
        headStyles: headStyles,
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 48, left: 5, right: 5 },
        tableWidth: 'auto',
        didParseCell: (data: any) => { if (useArabicFont) { data.cell.styles.font = 'Amiri'; data.cell.styles.fontStyle = 'normal'; } },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      }
      doc.save(`attendance-report-${new Date().toISOString().split('T')[0]}.pdf`);
      notifySuccess(t('reports.messages.export.pdf.success'));
    }).catch(() => notifyError(t('reports.messages.export.pdf.error')));
  };

  useEffect(() => {
    if (filters.building_id) {
      fetchFlatsByBuilding(Number(filters.building_id));
    } else {
      setFlats([]);
      setFilters(prev => ({ ...prev, flat_id: '' }));
    }
  }, [filters.building_id]);

  useEffect(() => {
    if (expenseFilters.building_id) {
      fetchExpenseFlatsByBuilding(Number(expenseFilters.building_id));
    } else {
      setExpenseFlats([]);
      setExpenseFilters(prev => ({ ...prev, flat_id: '' }));
    }
  }, [expenseFilters.building_id]);

  const fetchFlatsByBuilding = async (buildingId: number) => {
    try {
      const response = await api.get(`/reports/filter-options?building_id=${buildingId}`);
      let data: Flat[] = [];
      if (response.data && Array.isArray(response.data.flats)) {
        data = response.data.flats;
      } else {
        const fallbackRes = await api.get(`/buildings/${buildingId}/flats`);
        data = Array.isArray(fallbackRes.data) ? fallbackRes.data : [];
      }
      data.sort((a, b) => a.flat_number.localeCompare(b.flat_number, undefined, { numeric: true }));
      setFlats(data);
    } catch (error) {
      console.error('Error fetching flats:', error);
      setFlats([]);
    }
  };

  const fetchExpenseFlatsByBuilding = async (buildingId: number) => {
    try {
      const response = await api.get(`/reports/expense-filter-options?building_id=${buildingId}`);
      if (response.data && Array.isArray(response.data.flats)) {
        setExpenseFlats(response.data.flats);
      } else {
        // Fallback to direct API call
        const fallbackRes = await api.get(`/buildings/${buildingId}/flats`);
        setExpenseFlats(Array.isArray(fallbackRes.data) ? fallbackRes.data : []);
      }
    } catch (error) {
      console.error('Error fetching expense flats:', error);
      setExpenseFlats([]);
    }
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // --- Sorting logic ---
  const handleWoSort = (field: WOSortField) => {
    if (field === woSortField) {
      setWoSortDir(woSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setWoSortField(field);
      setWoSortDir('asc');
    }
  };

  const handleExpSort = (field: ExpenseSortField) => {
    if (field === expSortField) {
      setExpSortDir(expSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setExpSortField(field);
      setExpSortDir('asc');
    }
  };

  const handleCiSort = (field: CheckInSortField) => {
    if (field === ciSortField) {
      setCiSortDir(ciSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setCiSortField(field);
      setCiSortDir('asc');
    }
  };

  const handleDaSort = (field: DASortField) => {
    if (field === daSortField) {
      setDaSortDir(daSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setDaSortField(field);
      setDaSortDir('asc');
    }
  };

  const getSortedDailyActivity = () => {
    return [...dailyActivityData].sort((a: any, b: any) => {
      let valA: any, valB: any;
      switch (daSortField) {
        case 'time': case 'start_time': valA = a.start_time ? new Date(a.start_time).getTime() : 0; valB = b.start_time ? new Date(b.start_time).getTime() : 0; break;
        case 'type': valA = a.type || ''; valB = b.type || ''; break;
        case 'technician': valA = a.technician || ''; valB = b.technician || ''; break;
        case 'reference': valA = a.reference || ''; valB = b.reference || ''; break;
        case 'title': valA = a.title || ''; valB = b.title || ''; break;
        case 'building': valA = a.building || ''; valB = b.building || ''; break;
        case 'status': valA = a.status || ''; valB = b.status || ''; break;
        case 'end_time': valA = a.end_time ? new Date(a.end_time).getTime() : 0; valB = b.end_time ? new Date(b.end_time).getTime() : 0; break;
        case 'duration': valA = a.duration_minutes ?? -1; valB = b.duration_minutes ?? -1; break;
        default: valA = 0; valB = 0;
      }
      if (typeof valA === 'string') return daSortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      return daSortDir === 'asc' ? valA - valB : valB - valA;
    });
  };

  const getSortedReportData = () => {
    return [...reportData].sort((a, b) => {
      let valA: any, valB: any;
      const priorityOrder: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };
      switch (woSortField) {
        case 'work_order_number': valA = a.work_order_number || ''; valB = b.work_order_number || ''; break;
        case 'title':
          valA = (language === 'ar' && a.title_ar ? a.title_ar : a.title) || '';
          valB = (language === 'ar' && b.title_ar ? b.title_ar : b.title) || ''; break;
        case 'building':
          valA = (language === 'ar' ? a.flat?.building?.name_ar : a.flat?.building?.name) || '';
          valB = (language === 'ar' ? b.flat?.building?.name_ar : b.flat?.building?.name) || ''; break;
        case 'flat': valA = a.flat?.flat_number || ''; valB = b.flat?.flat_number || ''; break;
        case 'provider':
          valA = (language === 'ar' && a.technician?.user?.name_ar ? a.technician.user.name_ar : a.technician?.user?.name) || '';
          valB = (language === 'ar' && b.technician?.user?.name_ar ? b.technician.user.name_ar : b.technician?.user?.name) || ''; break;
        case 'status': valA = a.status || ''; valB = b.status || ''; break;
        case 'priority': valA = priorityOrder[a.priority] || 0; valB = priorityOrder[b.priority] || 0; break;
        case 'created_by': valA = a.created_by?.name || ''; valB = b.created_by?.name || ''; break;
        case 'created_at': valA = a.created_at ? new Date(a.created_at).getTime() : 0; valB = b.created_at ? new Date(b.created_at).getTime() : 0; break;
        case 'scheduled_from': valA = a.scheduled_from ? new Date(a.scheduled_from).getTime() : 0; valB = b.scheduled_from ? new Date(b.scheduled_from).getTime() : 0; break;
        case 'scheduled_to': valA = a.scheduled_to ? new Date(a.scheduled_to).getTime() : 0; valB = b.scheduled_to ? new Date(b.scheduled_to).getTime() : 0; break;
        case 'completed_date': valA = a.completed_date ? new Date(a.completed_date).getTime() : 0; valB = b.completed_date ? new Date(b.completed_date).getTime() : 0; break;
        case 'check_in_time':
          valA = a.check_ins?.[0]?.check_in_time ? new Date(a.check_ins[0].check_in_time).getTime() : 0;
          valB = b.check_ins?.[0]?.check_in_time ? new Date(b.check_ins[0].check_in_time).getTime() : 0; break;
        case 'check_out_time':
          valA = a.check_ins?.[0]?.check_out_time ? new Date(a.check_ins[0].check_out_time).getTime() : 0;
          valB = b.check_ins?.[0]?.check_out_time ? new Date(b.check_ins[0].check_out_time).getTime() : 0; break;
        case 'wo_duration':
          valA = a.check_ins?.[0]?.check_in_time && a.check_ins?.[0]?.check_out_time ? new Date(a.check_ins[0].check_out_time).getTime() - new Date(a.check_ins[0].check_in_time).getTime() : 0;
          valB = b.check_ins?.[0]?.check_in_time && b.check_ins?.[0]?.check_out_time ? new Date(b.check_ins[0].check_out_time).getTime() - new Date(b.check_ins[0].check_in_time).getTime() : 0; break;
        case 'pause_count':
          valA = a.pauses?.filter(p => p.action === 'pause').length || 0;
          valB = b.pauses?.filter(p => p.action === 'pause').length || 0; break;
        default: valA = ''; valB = '';
      }
      if (typeof valA === 'string') {
        const cmp = valA.localeCompare(valB, language === 'ar' ? 'ar' : 'en', { sensitivity: 'base' });
        return woSortDir === 'asc' ? cmp : -cmp;
      }
      return woSortDir === 'asc' ? valA - valB : valB - valA;
    });
  };

  const getSortedExpenseData = () => {
    return [...expenseReportData].sort((a, b) => {
      let valA: any, valB: any;
      switch (expSortField) {
        case 'expense_number': valA = a.expense_number || ''; valB = b.expense_number || ''; break;
        case 'title': valA = a.title || ''; valB = b.title || ''; break;
        case 'work_order_number': valA = a.work_order?.work_order_number || ''; valB = b.work_order?.work_order_number || ''; break;
        case 'service_type': valA = a.service_type || a.work_order?.technician?.service_type || ''; valB = b.service_type || b.work_order?.technician?.service_type || ''; break;
        case 'building':
          valA = (language === 'ar' ? (a.building?.name_ar || a.work_order?.flat?.building?.name_ar) : (a.building?.name || a.work_order?.flat?.building?.name)) || '';
          valB = (language === 'ar' ? (b.building?.name_ar || b.work_order?.flat?.building?.name_ar) : (b.building?.name || b.work_order?.flat?.building?.name)) || ''; break;
        case 'vendor': valA = a.vendor || ''; valB = b.vendor || ''; break;
        case 'amount': valA = parseFloat(a.amount) || 0; valB = parseFloat(b.amount) || 0; break;
        case 'expense_date': valA = a.expense_date ? new Date(a.expense_date).getTime() : 0; valB = b.expense_date ? new Date(b.expense_date).getTime() : 0; break;
        default: valA = ''; valB = '';
      }
      if (typeof valA === 'string') {
        const cmp = valA.localeCompare(valB, language === 'ar' ? 'ar' : 'en', { sensitivity: 'base' });
        return expSortDir === 'asc' ? cmp : -cmp;
      }
      return expSortDir === 'asc' ? valA - valB : valB - valA;
    });
  };

  const getSortedCheckInData = () => {
    return [...checkInReportData].sort((a, b) => {
      let valA: any, valB: any;
      switch (ciSortField) {
        case 'technician':
          valA = (language === 'ar' && a.user?.name_ar ? a.user.name_ar : a.user?.name) || '';
          valB = (language === 'ar' && b.user?.name_ar ? b.user.name_ar : b.user?.name) || ''; break;
        case 'building':
          valA = (language === 'ar' && a.building?.name_ar ? a.building.name_ar : a.building?.name) || '';
          valB = (language === 'ar' && b.building?.name_ar ? b.building.name_ar : b.building?.name) || ''; break;
        case 'check_in_time': valA = a.check_in_time ? new Date(a.check_in_time).getTime() : 0; valB = b.check_in_time ? new Date(b.check_in_time).getTime() : 0; break;
        case 'check_out_time': valA = a.check_out_time ? new Date(a.check_out_time).getTime() : 0; valB = b.check_out_time ? new Date(b.check_out_time).getTime() : 0; break;
        case 'duration':
          valA = a.check_in_time && a.check_out_time ? new Date(a.check_out_time).getTime() - new Date(a.check_in_time).getTime() : 0;
          valB = b.check_in_time && b.check_out_time ? new Date(b.check_out_time).getTime() - new Date(b.check_in_time).getTime() : 0; break;
        case 'status': valA = a.check_out_time ? 1 : 0; valB = b.check_out_time ? 1 : 0; break;
        default: valA = ''; valB = '';
      }
      if (typeof valA === 'string') {
        const cmp = valA.localeCompare(valB, language === 'ar' ? 'ar' : 'en', { sensitivity: 'base' });
        return ciSortDir === 'asc' ? cmp : -cmp;
      }
      return ciSortDir === 'asc' ? valA - valB : valB - valA;
    });
  };

  const linkSx = {
    color: 'primary.main',
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': { color: 'primary.dark' },
  } as const;

  const sortLabelSx = {
    '& .MuiTableSortLabel-icon': { opacity: 0.5 },
    '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
  } as const;

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Send client timezone offset (in minutes) so backend can interpret date filters correctly
      const tzOffset = new Date().getTimezoneOffset();
      params.append('tz_offset', tzOffset.toString());

      if (filters.technician_id) params.append('technician_id', filters.technician_id);
      if (filters.status.length > 0) params.append('status', filters.status.join(','));
      if (filters.priority.length > 0) params.append('priority', filters.priority.join(','));
      if (filters.service_type) params.append('service_type', filters.service_type);
      if (filters.created_by) params.append('created_by', filters.created_by);
      if (filters.building_id) params.append('building_id', filters.building_id);
      if (filters.flat_id) params.append('flat_id', filters.flat_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.scheduled_from) params.append('scheduled_from', filters.scheduled_from);
      if (filters.scheduled_to) params.append('scheduled_to', filters.scheduled_to);
      if (filters.completed_from) params.append('completed_from', filters.completed_from);
      if (filters.completed_to) params.append('completed_to', filters.completed_to);
      // Send activity types (date_from/date_to already sent above will be used for activity filtering)
      if (filters.activity_types.length > 0) {
        params.append('activity_types', filters.activity_types.join(','));
      }

      const response = await api.get(`/reports/work-orders?${params.toString()}`);
      setReportData(Array.isArray(response.data) ? response.data : []);
      setWoSortField('created_at');
      setWoSortDir('desc');
      notifySuccess(
        t('reports.messages.generate.success').replace(
          '{{count}}',
          String(response.data.length || 0)
        )
      );
    } catch (error: any) {
      console.error('Error generating report:', error);
      notifyError(
        error?.response?.data?.error || t('reports.messages.generate.error')
      );
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    if (reportData.length === 0) {
      notifyError(t('reports.messages.export.noData'));
      return;
    }

    // Dynamic import of jsPDF
    Promise.all([
      import('jspdf' as any),
      import('jspdf-autotable' as any)
    ]).then(async ([jsPDFModule, autoTableModule]: any[]) => {
      const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF || jsPDFModule;
      const autoTable = autoTableModule.default || autoTableModule;
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for better table display

      // Check if data contains Arabic text
      const dataContainsArabic = reportData.some(wo =>
        (wo.title && /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(wo.title)) ||
        (wo.title_ar && /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(wo.title_ar)) ||
        (wo.description && /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(wo.description))
      );

      // Load Arabic font if language is Arabic OR if data contains Arabic text
      let fontLoaded = false;
      if (language === 'ar' || dataContainsArabic) {
        fontLoaded = await loadArabicFont(doc);
        // Debug: Check available fonts
        const fontList = doc.getFontList();
        console.log('Available fonts:', Object.keys(fontList || {}));
        console.log('Amiri font loaded:', fontList && fontList['Amiri']);
      }
      
      // Header Section
      doc.setFillColor(25, 118, 210);
      doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
      
      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      // Set Arabic font for title if language is Arabic
      // Note: We only have 'normal' style, so use 'normal' even for bold text
      if (language === 'ar' && fontLoaded) {
        const fontList = doc.getFontList();
        if (fontList && fontList['Amiri']) {
          doc.setFont('Amiri', 'normal'); // Use 'normal' since we only loaded normal variant
          doc.setFontSize(20);
          console.log('Title font set to Amiri normal');
        } else {
          doc.setFont('helvetica', 'bold');
          console.log('Title font fallback to helvetica (Amiri not found)');
        }
      } else {
        doc.setFont('helvetica', 'bold');
      }
      doc.text(
        t('reports.tabs.workOrders'),
        doc.internal.pageSize.getWidth() / 2,
        18,
        { align: 'center' }
      );
      
      // Report Date
      doc.setFontSize(10);
      // Set Arabic font for date if language is Arabic
      if (language === 'ar' && fontLoaded) {
        const fontList = doc.getFontList();
        if (fontList && fontList['Amiri']) {
          doc.setFont('Amiri', 'normal');
        } else {
          doc.setFont('helvetica', 'normal');
        }
      } else {
        doc.setFont('helvetica', 'normal');
      }
      const generatedDate = new Date().toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      doc.text(
        `${t('reports.generatedOn')} ${generatedDate}`,
        doc.internal.pageSize.getWidth() / 2,
        25,
        { align: 'center' }
      );
      
      // Summary Info
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      // Set Arabic font for summary if language is Arabic
      if (language === 'ar' && fontLoaded) {
        const fontList = doc.getFontList();
        if (fontList && fontList['Amiri']) {
          doc.setFont('Amiri', 'normal');
        } else {
          doc.setFont('helvetica', 'bold');
        }
      } else {
        doc.setFont('helvetica', 'bold');
      }
      doc.text(`${t('reports.totalRecords')} ${reportData.length}`, 10, 40);
      
      // Prepare table data - include all key columns; long text will wrap to multiple lines
      const tableData = reportData.map(wo => {
        const localeDate = (value: string | null | undefined, withTime: boolean = false) => {
          if (!value) return 'N/A';
          const d = new Date(value);
          const locale = language === 'ar' ? 'ar-SA' : 'en-US';
          const base: any = { year: 'numeric', month: 'short', day: 'numeric' };
          if (withTime) {
            base.hour = '2-digit';
            base.minute = '2-digit';
          }
          return d.toLocaleDateString(locale, base);
        };
        return [
          wo.work_order_number,
          (language === 'ar' && wo.title_ar ? wo.title_ar : wo.title) || 'N/A',
          wo.description || 'N/A',
          (language === 'ar' && wo.flat?.building?.name_ar ? wo.flat.building.name_ar : wo.flat?.building?.name) || 'N/A',
          wo.flat?.flat_number || 'N/A',
          (language === 'ar' && wo.technician?.user?.name_ar ? wo.technician.user.name_ar : wo.technician?.user?.name) || 'N/A',
          t(`workOrders.status.${wo.status === 'in_progress' ? 'inProgress' : wo.status === 'paused' ? 'paused' : wo.status}`),
          t(`workOrders.priority.${wo.priority}`),
          (language === 'ar' && wo.created_by?.name_ar ? wo.created_by.name_ar : wo.created_by?.name) || 'N/A',
          localeDate(wo.created_at),
          localeDate(wo.scheduled_from || null),
          localeDate(wo.scheduled_to || null),
          localeDate(wo.completed_date || null),
          localeDate(wo.check_ins?.[0]?.check_in_time, true),
          localeDate(wo.check_ins?.[0]?.check_out_time, true),
          wo.check_ins?.[0]?.check_in_time && wo.check_ins?.[0]?.check_out_time ? formatDuration(wo.check_ins[0].check_in_time, wo.check_ins[0].check_out_time) : 'N/A',
          String(wo.pauses?.filter(p => p.action === 'pause').length || 0),
        ];
      });

      // Add table with professional styling
      const tableStyles: any = {
        fontSize: 6,
        cellPadding: 1.5,
        overflow: 'linebreak',
        halign: 'center',
        valign: 'middle',
      };
      
      // Set Arabic font for table if language is Arabic or data contains Arabic
      const fontList = doc.getFontList();
      const hasArabicFont = fontList && fontList['Amiri'];
      const useArabicFont = (language === 'ar' || dataContainsArabic) && hasArabicFont;
      console.log('Table - hasArabicFont:', hasArabicFont, 'useArabicFont:', useArabicFont);

      // Set font globally before autoTable if Arabic font needed
      if (useArabicFont) {
        doc.setFont('Amiri', 'normal');
        tableStyles.font = 'Amiri';
        tableStyles.fontStyle = 'normal';
        tableStyles.useUnicode = true;
        console.log('Table font set to Amiri');
      }

      // Prepare head styles with Arabic font if needed
      const headStyles: any = {
        fillColor: [25, 118, 210],
        textColor: [255, 255, 255],
        fontStyle: 'normal',
        halign: 'center',
        valign: 'middle',
        fontSize: 6,
      };

      if (useArabicFont) {
        headStyles.font = 'Amiri';
        headStyles.fontStyle = 'normal';
        headStyles.useUnicode = true;
        headStyles.lang = 'ar';
      }
      
      autoTable(doc, {
        head: [[
          t('workOrders.export.columns.number'),
          t('workOrders.export.columns.title'),
          t('workOrders.export.columns.description'),
          t('workOrders.export.columns.building'),
          t('workOrders.export.columns.flat'),
          t('workOrders.export.columns.provider'),
          t('workOrders.export.columns.status'),
          t('workOrders.export.columns.priority'),
          t('workOrders.list.columns.createdBy') || 'Created By',
          t('workOrders.list.columns.createdDate') || 'Created Date',
          t('workOrders.list.columns.scheduledFrom') || 'Scheduled From',
          t('workOrders.list.columns.scheduledTo') || 'Scheduled To',
          t('workOrders.list.columns.completedDate') || 'Completed Date',
          t('reports.table.checkInTime'),
          t('reports.table.checkOutTime'),
          t('reports.table.duration'),
          t('reports.table.pauseCount'),
        ]],
        body: tableData,
        startY: 48,
        styles: tableStyles,
        headStyles: headStyles,
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: (() => {
          const baseStyles: any = {
            0: { cellWidth: 20 },  // WO#
            1: { cellWidth: 20 },      // Title (fixed width)
            2: { cellWidth: 20 },      // Description (fixed width, wider)
            3: { cellWidth: 20 },  // Building
            4: { cellWidth: 20 },  // Flat
            5: { cellWidth: 20 },      // Technician / Provider (fixed width)
            6: { cellWidth: 'auto' },  // Status
            7: { cellWidth: 'auto' },  // Priority
            8: { cellWidth: 15 },  // Created By
            9: { cellWidth: 15 },  // Created Date
            10: { cellWidth: 15 }, // Scheduled From
            11: { cellWidth: 15 }, // Scheduled To
            12: { cellWidth: 15 }, // Completed Date
            13: { cellWidth: 15 }, // Check-In Time
            14: { cellWidth: 15 }, // Check-Out Time
            15: { cellWidth: 15 }, // Duration
            16: { cellWidth: 15 }, // Pause Count
          };
          // Add Arabic font to all column styles if needed
          if (useArabicFont) {
            Object.keys(baseStyles).forEach(key => {
              baseStyles[key].font = 'Amiri';
              baseStyles[key].fontStyle = 'normal';
            });
          }
          return baseStyles;
        })(),
        margin: { top: 48, left: 5, right: 5 },
        tableWidth: 'wrap',
        didParseCell: (cellData: any) => {
          // Set Arabic font for header cells
          if (cellData.section === 'head' && useArabicFont) {
            cellData.cell.styles.font = 'Amiri';
            cellData.cell.styles.fontStyle = 'normal';
            // Also set it directly on the cell text properties
            if (cellData.cell.text && Array.isArray(cellData.cell.text)) {
              cellData.cell.text.forEach((text: any) => {
                if (text && typeof text === 'object') {
                  text.font = 'Amiri';
                }
              });
            }
          }
          // Set Arabic font for body cells if needed
          if (cellData.section === 'body' && useArabicFont) {
            cellData.cell.styles.font = 'Amiri';
            cellData.cell.styles.fontStyle = 'normal';
          }
        },
        didDrawCell: (cellData: any) => {
          // Force set Arabic font when drawing cells
          if (useArabicFont) {
            doc.setFont('Amiri', 'normal');
          }
        },
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      doc.save(`work-orders-report-${new Date().toISOString().split('T')[0]}.pdf`);
      notifySuccess(t('reports.messages.export.pdf.success'));
    }).catch(() => {
      notifyError(t('reports.messages.export.pdf.error'));
    });
  };

  const exportToExcel = () => {
    if (reportData.length === 0) {
      notifyError(t('reports.messages.export.noData'));
      return;
    }

    // Dynamic import of xlsx
    import('xlsx' as any).then((XLSX: any) => {
      // Prepare data with proper formatting
      const excelData = reportData.map(wo => {
        const lastPause = getLastPauseTime(wo);
        const lastResume = getLastResumeTime(wo);
        const locale = language === 'ar' ? 'ar-SA' : 'en-US';
        const asDate = (value: string | null | undefined, withTime: boolean = false) => {
          if (!value) return 'N/A';
          const d = new Date(value);
          const base: any = { year: 'numeric', month: 'short', day: 'numeric' };
          if (withTime) {
            base.hour = '2-digit';
            base.minute = '2-digit';
          }
          return d.toLocaleString(locale, base);
        };
        return ({
        [t('workOrders.export.columns.number')]: wo.work_order_number,
        [t('workOrders.export.columns.title')]: (language === 'ar' && wo.title_ar ? wo.title_ar : wo.title) || 'N/A',
        [t('workOrders.export.columns.building')]: (language === 'ar' && wo.flat?.building?.name_ar ? wo.flat.building.name_ar : wo.flat?.building?.name) || 'N/A',
        [t('workOrders.export.columns.flat')]: wo.flat?.flat_number || 'N/A',
        [t('workOrders.export.columns.provider')]: (language === 'ar' && wo.technician?.user?.name_ar ? wo.technician.user.name_ar : wo.technician?.user?.name) || 'N/A',
        [t('workOrders.export.columns.status')]: t(`workOrders.status.${wo.status === 'in_progress' ? 'inProgress' : wo.status === 'paused' ? 'paused' : wo.status}`),
        [t('workOrders.export.columns.priority')]: t(`workOrders.priority.${wo.priority}`),
        [t('workOrders.list.columns.createdBy') || 'Created By']: (language === 'ar' && wo.created_by?.name_ar ? wo.created_by.name_ar : wo.created_by?.name) || 'N/A',
        [t('workOrders.list.columns.createdDate') || 'Created Date']: asDate(wo.created_at),
        [t('workOrders.list.columns.scheduledFrom') || 'Scheduled From']: asDate(wo.scheduled_from || null),
        [t('workOrders.list.columns.scheduledTo') || 'Scheduled To']: asDate(wo.scheduled_to || null),
        [t('workOrders.list.columns.completedDate') || 'Completed Date']: asDate(wo.completed_date || null),
        [t('reports.table.checkInTime')]: asDate(wo.check_ins?.[0]?.check_in_time, true),
        [t('reports.table.checkOutTime')]: asDate(wo.check_ins?.[0]?.check_out_time, true),
        [t('reports.table.duration')]: wo.check_ins?.[0]?.check_in_time && wo.check_ins?.[0]?.check_out_time ? formatDuration(wo.check_ins[0].check_in_time, wo.check_ins[0].check_out_time) : 'N/A',
        [t('reports.table.pauseCount')]: wo.pauses?.filter(p => p.action === 'pause').length || 0,
        [t('reports.table.lastPause')]: asDate(lastPause, true),
        [t('reports.table.lastResume')]: asDate(lastResume, true),
        [t('workOrders.form.description')]: wo.description || 'N/A',
      });
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths for better readability
      const colWidths = [
        { wch: 15 }, // Work Order #
        { wch: 30 }, // Title
        { wch: 20 }, // Building
        { wch: 10 }, // Flat
        { wch: 25 }, // Technician
        { wch: 15 }, // Status
        { wch: 12 }, // Priority
        { wch: 20 }, // Created By
        { wch: 18 }, // Created Date
        { wch: 18 }, // Scheduled From
        { wch: 18 }, // Scheduled To
        { wch: 18 }, // Completed Date
        { wch: 22 }, // Check-In Time
        { wch: 22 }, // Check-Out Time
        { wch: 14 }, // Duration
        { wch: 14 }, // Pause Count
        { wch: 22 }, // Last Pause
        { wch: 22 }, // Last Resume
        { wch: 40 }, // Description
      ];
      ws['!cols'] = colWidths;
      
      // Style header row (bold)
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '1976D2' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Work Orders');

      // Add summary sheet
      const summaryData = [
        ['Report Summary'],
        ['Generated Date', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })],
        ['Total Work Orders', reportData.length],
        [''],
        ['Status Breakdown'],
      ];
      
      const statusCounts: { [key: string]: number } = {};
      reportData.forEach(wo => {
        statusCounts[wo.status] = (statusCounts[wo.status] || 0) + 1;
      });
      
      Object.entries(statusCounts).forEach(([status, count]) => {
        summaryData.push([status.replace('_', ' ').toUpperCase(), count]);
      });
      
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      summaryWs['!cols'] = [{ wch: 25 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

      // Save file
      XLSX.writeFile(wb, `work-orders-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      notifySuccess(t('reports.messages.export.excel.success'));
    }).catch(() => {
      notifyError(t('reports.messages.export.excel.error'));
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const generateExpenseReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (expenseFilters.work_order_id) params.append('work_order_id', expenseFilters.work_order_id);
      if (expenseFilters.service_type) params.append('service_type', expenseFilters.service_type);
      if (expenseFilters.building_id) params.append('building_id', expenseFilters.building_id);
      if (expenseFilters.flat_id) params.append('flat_id', expenseFilters.flat_id);
      if (expenseFilters.vendor) params.append('vendor', expenseFilters.vendor);
      if (expenseFilters.date_from) params.append('date_from', expenseFilters.date_from);
      if (expenseFilters.date_to) params.append('date_to', expenseFilters.date_to);
      if (expenseFilters.amount_min) params.append('amount_min', expenseFilters.amount_min);
      if (expenseFilters.amount_max) params.append('amount_max', expenseFilters.amount_max);

      const response = await api.get(`/reports/expenses?${params.toString()}`);
      setExpenseReportData(Array.isArray(response.data) ? response.data : []);
      setExpSortField('expense_date');
      setExpSortDir('desc');
      notifySuccess(
        t('reports.messages.generate.success').replace('{{count}}', String(response.data.length || 0))
      );
    } catch (error: any) {
      console.error('Error generating expense report:', error);
      notifyError(error?.response?.data?.error || t('reports.messages.generate.error'));
      setExpenseReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const exportExpensesToPDF = () => {
    if (expenseReportData.length === 0) {
      notifyError(t('reports.messages.export.noData'));
      return;
    }

    Promise.all([
      import('jspdf' as any),
      import('jspdf-autotable' as any)
    ]).then(async ([jsPDFModule, autoTableModule]: any[]) => {
      const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF || jsPDFModule;
      const autoTable = autoTableModule.default || autoTableModule;
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Check if data contains Arabic text
      const expenseDataContainsArabic = expenseReportData.some(exp =>
        (exp.notes && /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(exp.notes)) ||
        (exp.title && /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(exp.title)) ||
        (exp.vendor && /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(exp.vendor))
      );

      // Load Arabic font if language is Arabic OR if data contains Arabic text
      let expenseFontLoaded = false;
      if (language === 'ar' || expenseDataContainsArabic) {
        expenseFontLoaded = await loadArabicFont(doc);
      }
      
      // Check if Arabic font is available
      const expenseFontList = doc.getFontList();
      const expenseHasArabicFont = expenseFontList && expenseFontList['Amiri'];
      const useExpenseArabicFont = (language === 'ar' || expenseDataContainsArabic) && expenseHasArabicFont;
      
      doc.setFillColor(25, 118, 210);
      doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      // Set Arabic font for title if language is Arabic
      // Note: We only have 'normal' style, so use 'normal' even for bold text
      if (language === 'ar' && expenseFontLoaded && expenseHasArabicFont) {
        doc.setFont('Amiri', 'normal'); // Use 'normal' since we only loaded normal variant
        doc.setFontSize(20);
        console.log('Expense title font set to Amiri normal');
      } else {
        doc.setFont('helvetica', 'bold');
        console.log('Expense title font set to helvetica (fallback)');
      }
      doc.text(
        t('reports.tabs.expenses'),
        doc.internal.pageSize.getWidth() / 2,
        18,
        { align: 'center' }
      );
      
      doc.setFontSize(10);
      // Set Arabic font for date if language is Arabic
      if (language === 'ar' && expenseHasArabicFont) {
        const fontList = doc.getFontList();
        if (fontList && fontList['Amiri']) {
          doc.setFont('Amiri', 'normal');
        } else {
          doc.setFont('helvetica', 'normal');
        }
      } else {
        doc.setFont('helvetica', 'normal');
      }
      const expenseGeneratedDate = new Date().toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      doc.text(`${t('reports.generatedOn')} ${expenseGeneratedDate}`, doc.internal.pageSize.getWidth() / 2, 25, { align: 'center' });
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      // Set Arabic font for summary if language is Arabic
      if (language === 'ar' && expenseHasArabicFont) {
        const fontList = doc.getFontList();
        if (fontList && fontList['Amiri']) {
          doc.setFont('Amiri', 'normal');
        } else {
          doc.setFont('helvetica', 'bold');
        }
      } else {
        doc.setFont('helvetica', 'bold');
      }
      const totalAmount = expenseReportData.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
      doc.text(`${t('reports.totalRecords')} ${expenseReportData.length} | ${t('reports.totalAmount')} ${totalAmount.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KD`, 10, 40);
      
      const tableData = expenseReportData.map(exp => {
        const serviceType = exp.service_type || exp.work_order?.technician?.service_type || '';
        const serviceTypeObj = serviceTypesObjects.find(st => st.name_en === serviceType);
        const serviceTypeDisplay = serviceTypeObj ? (language === 'ar' ? serviceTypeObj.name_ar : serviceTypeObj.name_en) : (serviceType || 'N/A');
        
        const buildingName = exp.building?.name_ar || exp.work_order?.flat?.building?.name_ar || exp.building?.name || exp.work_order?.flat?.building?.name || 'N/A';
        const displayBuildingName = language === 'ar' && (exp.building?.name_ar || exp.work_order?.flat?.building?.name_ar) ? buildingName : (exp.building?.name || exp.work_order?.flat?.building?.name || 'N/A');
        
        return [
          exp.expense_number,
          exp.title || 'N/A',
          exp.notes || 'N/A',
          exp.work_order?.work_order_number || 'N/A',
          serviceTypeDisplay,
          displayBuildingName,
          exp.flat?.flat_number || exp.work_order?.flat?.flat_number || 'N/A',
          exp.vendor || 'N/A',
          parseFloat(exp.amount || 0).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' KD',
          exp.expense_date ? new Date(exp.expense_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A',
        ];
      });

      const expenseTableStyles: any = { 
        fontSize: 7, 
        cellPadding: 2, 
        overflow: 'linebreak', 
        cellWidth: 'wrap', 
        halign: 'center', 
        valign: 'middle' 
      };
      
      // Set font globally before autoTable if Arabic
      if (useExpenseArabicFont) {
        doc.setFont('Amiri', 'normal');
        expenseTableStyles.font = 'Amiri';
        expenseTableStyles.fontStyle = 'normal';
        expenseTableStyles.useUnicode = true;
        expenseTableStyles.lang = 'ar';
      }

      // Prepare head styles with Arabic font if needed
      const expenseHeadStyles: any = {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap',
        halign: 'center',
        valign: 'middle',
        fillColor: [25, 118, 210],
        textColor: [255, 255, 255],
        fontStyle: 'normal',
      };

      if (useExpenseArabicFont) {
        expenseHeadStyles.font = 'Amiri';
        expenseHeadStyles.fontStyle = 'normal'; // Use 'normal' since we only have normal variant
        expenseHeadStyles.useUnicode = true;
        expenseHeadStyles.lang = 'ar';
      }
      
      autoTable(doc, {
        head: [[
          t('expenses.export.columns.number'),
          t('expenses.dialog.title'),
          t('reports.table.description'),
          t('expenses.export.columns.workOrder'),
          t('expenses.export.columns.serviceType'),
          t('expenses.export.columns.building'),
          t('expenses.export.columns.flat'),
          t('expenses.dialog.vendor'),
          t('expenses.export.columns.amount'),
          t('expenses.export.columns.date')
        ]],
        body: tableData,
        startY: 48,
        styles: expenseTableStyles,
        headStyles: expenseHeadStyles,
        columnStyles: {
          0: { cellWidth: 'auto' }, // Expense #
          1: { cellWidth: 40 },     // Title (fixed width)
          2: { cellWidth: 60 },     // Description/Notes (fixed width, wider)
          3: { cellWidth: 'auto' }, // Work Order
          4: { cellWidth: 'auto' }, // Service Type
          5: { cellWidth: 'auto' }, // Building
          6: { cellWidth: 'auto' }, // Flat
          7: { cellWidth: 'auto' }, // Vendor
          8: { cellWidth: 'auto' }, // Amount
          9: { cellWidth: 'auto' }, // Date
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 48, left: 5, right: 5 },
        tableWidth: 'auto',
        didParseCell: (data: any) => {
          // Set Arabic font for header cells
          if (data.section === 'head' && useExpenseArabicFont) {
            data.cell.styles.font = 'Amiri';
            data.cell.styles.fontStyle = 'normal';
          }
          // Set Arabic font for body cells if needed
          if (data.section === 'body' && useExpenseArabicFont) {
            data.cell.styles.font = 'Amiri';
            data.cell.styles.fontStyle = 'normal';
          }
        },
        willDrawCell: (data: any) => {
          // Force set Arabic font BEFORE drawing each cell
          if (useExpenseArabicFont) {
            doc.setFont('Amiri', 'normal');
          }
        },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      }

      doc.save(`expenses-report-${new Date().toISOString().split('T')[0]}.pdf`);
      notifySuccess(t('reports.messages.export.pdf.success'));
    }).catch(() => {
      notifyError(t('reports.messages.export.pdf.error'));
    });
  };

  const exportExpensesToExcel = () => {
    if (expenseReportData.length === 0) {
      notifyError(t('reports.messages.export.noData'));
      return;
    }

    import('xlsx' as any).then((XLSX: any) => {
      const excelData = expenseReportData.map(exp => {
        const serviceType = exp.service_type || exp.work_order?.technician?.service_type || '';
        const serviceTypeObj = serviceTypesObjects.find(st => st.name_en === serviceType);
        const serviceTypeDisplay = serviceTypeObj ? (language === 'ar' ? serviceTypeObj.name_ar : serviceTypeObj.name_en) : (serviceType || 'N/A');
        
        const buildingName = exp.building?.name_ar || exp.work_order?.flat?.building?.name_ar || exp.building?.name || exp.work_order?.flat?.building?.name || 'N/A';
        const displayBuildingName = language === 'ar' && (exp.building?.name_ar || exp.work_order?.flat?.building?.name_ar) ? buildingName : (exp.building?.name || exp.work_order?.flat?.building?.name || 'N/A');
        
        return {
          [t('expenses.export.columns.number')]: exp.expense_number,
          [t('expenses.dialog.title')]: exp.title || 'N/A',
          [t('expenses.export.columns.workOrder')]: exp.work_order?.work_order_number || 'N/A',
          [t('expenses.export.columns.serviceType')]: serviceTypeDisplay,
          [t('expenses.export.columns.building')]: displayBuildingName,
          [t('expenses.export.columns.flat')]: exp.flat?.flat_number || exp.work_order?.flat?.flat_number || 'N/A',
          [t('expenses.dialog.vendor')]: exp.vendor || 'N/A',
          [t('expenses.export.columns.amount')]: parseFloat(exp.amount || 0).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          [t('expenses.export.columns.date')]: exp.expense_date ? new Date(exp.expense_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A',
          [t('expenses.dialog.notes')]: exp.notes || 'N/A',
        };
      });

      const ws = XLSX.utils.json_to_sheet(excelData);
      const colWidths = [
        { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 10 },
        { wch: 20 }, { wch: 15 }, { wch: 18 }, { wch: 40 },
      ];
      ws['!cols'] = colWidths;
      
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '1976D2' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Expenses');

      const totalAmount = expenseReportData.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
      const summaryData = [
        ['Report Summary'],
        ['Generated Date', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })],
        ['Total Expenses', expenseReportData.length],
        ['Total Amount (KD)', totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
      ];
      
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      summaryWs['!cols'] = [{ wch: 25 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

      XLSX.writeFile(wb, `expenses-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      notifySuccess(t('reports.messages.export.excel.success'));
    }).catch(() => {
      notifyError(t('reports.messages.export.excel.error'));
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // ---- Quick Check-In Report ----
  const generateCheckInReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (checkInFilters.user_id) params.append('user_id', checkInFilters.user_id);
      if (checkInFilters.building_id) params.append('building_id', checkInFilters.building_id);
      if (checkInFilters.status) params.append('status', checkInFilters.status);
      if (checkInFilters.date_from) params.append('date_from', checkInFilters.date_from);
      if (checkInFilters.date_to) params.append('date_to', checkInFilters.date_to);

      const response = await api.get(`/reports/check-ins?${params.toString()}`);
      setCheckInReportData(Array.isArray(response.data) ? response.data : []);
      setCiSortField('check_in_time');
      setCiSortDir('desc');
      notifySuccess(t('reports.messages.generate.success').replace('{{count}}', String(response.data.length || 0)));
    } catch (error: any) {
      console.error('Error generating check-in report:', error);
      notifyError(error?.response?.data?.error || t('reports.messages.generate.error'));
      setCheckInReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (checkIn: string, checkOut: string) => {
    const ms = new Date(checkOut).getTime() - new Date(checkIn).getTime();
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  };

  const exportCheckInsToPDF = () => {
    if (checkInReportData.length === 0) {
      notifyError(t('reports.messages.export.noData'));
      return;
    }

    Promise.all([
      import('jspdf' as any),
      import('jspdf-autotable' as any)
    ]).then(async ([jsPDFModule, autoTableModule]: any[]) => {
      const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF || jsPDFModule;
      const autoTable = autoTableModule.default || autoTableModule;
      const doc = new jsPDF('l', 'mm', 'a4');

      const dataContainsArabic = checkInReportData.some(ci =>
        (ci.building?.name_ar && /[\u0600-\u06FF]/.test(ci.building.name_ar)) ||
        (ci.check_in_notes && /[\u0600-\u06FF]/.test(ci.check_in_notes))
      );

      let fontLoaded = false;
      if (language === 'ar' || dataContainsArabic) {
        fontLoaded = await loadArabicFont(doc);
      }

      const fontList = doc.getFontList();
      const hasArabicFont = fontList && fontList['Amiri'];
      const useArabicFont = (language === 'ar' || dataContainsArabic) && hasArabicFont;

      // Header
      doc.setFillColor(25, 118, 210);
      doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      if (useArabicFont) { doc.setFont('Amiri', 'normal'); } else { doc.setFont('helvetica', 'bold'); }
      doc.text(t('reports.tabs.checkIns'), doc.internal.pageSize.getWidth() / 2, 18, { align: 'center' });

      doc.setFontSize(10);
      if (useArabicFont) { doc.setFont('Amiri', 'normal'); } else { doc.setFont('helvetica', 'normal'); }
      const generatedDate = new Date().toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      doc.text(`${t('reports.generatedOn')} ${generatedDate}`, doc.internal.pageSize.getWidth() / 2, 25, { align: 'center' });

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      if (useArabicFont) { doc.setFont('Amiri', 'normal'); } else { doc.setFont('helvetica', 'bold'); }
      doc.text(`${t('reports.totalRecords')} ${checkInReportData.length}`, 10, 40);

      const tableData = checkInReportData.map((ci: any) => [
        (language === 'ar' && ci.user?.name_ar ? ci.user.name_ar : ci.user?.name) || 'N/A',
        (language === 'ar' && ci.building?.name_ar ? ci.building.name_ar : ci.building?.name) || 'N/A',
        ci.amenities?.map((a: any) => language === 'ar' ? a.name_ar : a.name_en).join(', ') || 'N/A',
        ci.selected_flats?.map((f: any) => f.flat_number).join(', ') || 'N/A',
        ci.check_in_time ? new Date(ci.check_in_time).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US') : 'N/A',
        ci.check_out_time ? new Date(ci.check_out_time).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US') : 'N/A',
        ci.check_in_time && ci.check_out_time ? formatDuration(ci.check_in_time, ci.check_out_time) : 'N/A',
        ci.check_out_time ? t('reports.checkIn.status.checkedOut') : t('reports.checkIn.status.checkedIn'),
        ci.check_in_notes || '',
      ]);

      const tableStyles: any = { fontSize: 7, cellPadding: 2, overflow: 'linebreak', cellWidth: 'wrap', halign: 'center', valign: 'middle' };
      const headStyles: any = { fillColor: [25, 118, 210], textColor: [255, 255, 255], fontStyle: 'normal', halign: 'center', valign: 'middle', fontSize: 7 };

      if (useArabicFont) {
        doc.setFont('Amiri', 'normal');
        tableStyles.font = 'Amiri'; tableStyles.fontStyle = 'normal';
        headStyles.font = 'Amiri'; headStyles.fontStyle = 'normal';
      }

      autoTable(doc, {
        head: [[
          t('reports.checkIn.table.technician'),
          t('reports.checkIn.table.building'),
          t('reports.checkIn.table.amenities'),
          t('reports.checkIn.table.flats'),
          t('reports.checkIn.table.checkInTime'),
          t('reports.checkIn.table.checkOutTime'),
          t('reports.checkIn.table.duration'),
          t('reports.checkIn.table.status'),
          t('reports.checkIn.table.notes'),
        ]],
        body: tableData,
        startY: 48,
        styles: tableStyles,
        headStyles: headStyles,
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 48, left: 5, right: 5 },
        tableWidth: 'auto',
        didParseCell: (data: any) => {
          if (useArabicFont) { data.cell.styles.font = 'Amiri'; data.cell.styles.fontStyle = 'normal'; }
        },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      }

      doc.save(`check-ins-report-${new Date().toISOString().split('T')[0]}.pdf`);
      notifySuccess(t('reports.messages.export.pdf.success'));
    }).catch(() => {
      notifyError(t('reports.messages.export.pdf.error'));
    });
  };

  // ---- Out-of-Site Tasks Report (exports) ----
  const exportOutOfSiteToPDF = () => {
    if (outOfSiteTasks.length === 0) {
      notifyError(t('reports.messages.export.noData'));
      return;
    }

    Promise.all([
      import('jspdf' as any),
      import('jspdf-autotable' as any),
    ]).then(async ([jsPDFModule, autoTableModule]: any[]) => {
      const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF || jsPDFModule;
      const autoTable = autoTableModule.default || autoTableModule;
      const doc = new jsPDF('l', 'mm', 'a4');

      const dataContainsArabic = outOfSiteTasks.some(task =>
        (task.technician?.user?.name_ar && /[\u0600-\u06FF]/.test(task.technician.user.name_ar)) ||
        (task.objective && /[\u0600-\u06FF]/.test(task.objective)),
      );

      let fontLoaded = false;
      if (language === 'ar' || dataContainsArabic) {
        fontLoaded = await loadArabicFont(doc);
      }

      const fontList = doc.getFontList();
      const hasArabicFont = fontList && fontList['Amiri'];
      const useArabicFont = (language === 'ar' || dataContainsArabic) && hasArabicFont;

      // Header
      doc.setFillColor(25, 118, 210);
      doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      if (useArabicFont && fontLoaded) {
        doc.setFont('Amiri', 'normal');
      } else {
        doc.setFont('helvetica', 'bold');
      }
      doc.text(
        t('reports.tabs.outOfSiteTasks'),
        doc.internal.pageSize.getWidth() / 2,
        18,
        { align: 'center' },
      );

      // Summary row
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      if (useArabicFont && hasArabicFont) {
        doc.setFont('Amiri', 'normal');
      } else {
        doc.setFont('helvetica', 'bold');
      }
      doc.text(`${t('reports.totalRecords')} ${outOfSiteTasks.length}`, 10, 40);

      const tableData = outOfSiteTasks.map((task) => [
        task.technician?.user
          ? (language === 'ar' && task.technician.user.name_ar
            ? task.technician.user.name_ar
            : task.technician.user.name)
          : 'N/A',
        task.objective || 'N/A',
        task.start_time ? formatDateTime(task.start_time, language) : 'N/A',
        task.expected_end_time ? formatDateTime(task.expected_end_time, language) : 'N/A',
        task.end_time ? formatDateTime(task.end_time, language) : 'N/A',
        task.status.replace('_', ' '),
      ]);

      const tableStyles: any = {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'center',
        valign: 'middle',
      };

      const headStyles: any = {
        fontSize: 7,
        cellPadding: 2,
        halign: 'center',
        valign: 'middle',
        fillColor: [25, 118, 210],
        textColor: [255, 255, 255],
        fontStyle: 'normal',
      };

      if (useArabicFont) {
        tableStyles.font = 'Amiri';
        tableStyles.fontStyle = 'normal';
        tableStyles.useUnicode = true;
        headStyles.font = 'Amiri';
        headStyles.fontStyle = 'normal';
        headStyles.useUnicode = true;
        headStyles.lang = 'ar';
      }

      autoTable(doc, {
        head: [[
          t('reports.table.provider'),
          t('reports.outOfSite.table.objective'),
          t('reports.outOfSite.table.startTime'),
          t('reports.outOfSite.table.expectedEnd'),
          t('reports.outOfSite.table.endTime'),
          t('reports.outOfSite.table.status'),
        ]],
        body: tableData,
        startY: 48,
        styles: tableStyles,
        headStyles,
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 48, left: 5, right: 5 },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' },
        );
      }

      doc.save(`out-of-site-tasks-report-${new Date().toISOString().split('T')[0]}.pdf`);
      notifySuccess(t('reports.messages.export.pdf.success'));
    }).catch(() => {
      notifyError(t('reports.messages.export.pdf.error'));
    });
  };

  const exportOutOfSiteToExcel = () => {
    if (outOfSiteTasks.length === 0) {
      notifyError(t('reports.messages.export.noData'));
      return;
    }

    import('xlsx' as any)
      .then((XLSX: any) => {
        const rows = outOfSiteTasks.map((task) => ({
          [t('reports.table.provider')]:
            task.technician?.user
              ? (language === 'ar' && task.technician.user.name_ar
                ? task.technician.user.name_ar
                : task.technician.user.name)
              : 'N/A',
          [t('reports.outOfSite.table.objective')]: task.objective || 'N/A',
          [t('reports.outOfSite.table.startTime')]: task.start_time ? formatDateTime(task.start_time, language) : 'N/A',
          [t('reports.outOfSite.table.expectedEnd')]: task.expected_end_time ? formatDateTime(task.expected_end_time, language) : 'N/A',
          [t('reports.outOfSite.table.endTime')]: task.end_time ? formatDateTime(task.end_time, language) : 'N/A',
          [t('reports.outOfSite.table.status')]: task.status.replace('_', ' '),
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [
          { wch: 25 }, // Technician
          { wch: 40 }, // Objective
          { wch: 22 }, // Start time
          { wch: 22 }, // Expected end
          { wch: 22 }, // End time
          { wch: 15 }, // Status
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Out-of-Site Tasks');

        XLSX.writeFile(wb, `out-of-site-tasks-report-${new Date().toISOString().split('T')[0]}.xlsx`);
        notifySuccess(t('reports.messages.export.excel.success'));
      })
      .catch(() => {
        notifyError(t('reports.messages.export.excel.error'));
      });
  };

  const exportCheckInsToExcel = () => {
    if (checkInReportData.length === 0) {
      notifyError(t('reports.messages.export.noData'));
      return;
    }

    import('xlsx' as any).then((XLSX: any) => {
      const excelData = checkInReportData.map((ci: any) => ({
        [t('reports.checkIn.table.technician')]: (language === 'ar' && ci.user?.name_ar ? ci.user.name_ar : ci.user?.name) || 'N/A',
        [t('reports.checkIn.table.building')]: (language === 'ar' && ci.building?.name_ar ? ci.building.name_ar : ci.building?.name) || 'N/A',
        [t('reports.checkIn.table.amenities')]: ci.amenities?.map((a: any) => language === 'ar' ? a.name_ar : a.name_en).join(', ') || 'N/A',
        [t('reports.checkIn.table.flats')]: ci.selected_flats?.map((f: any) => f.flat_number).join(', ') || 'N/A',
        [t('reports.checkIn.table.checkInTime')]: ci.check_in_time ? new Date(ci.check_in_time).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US') : 'N/A',
        [t('reports.checkIn.table.checkOutTime')]: ci.check_out_time ? new Date(ci.check_out_time).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US') : 'N/A',
        [t('reports.checkIn.table.duration')]: ci.check_in_time && ci.check_out_time ? formatDuration(ci.check_in_time, ci.check_out_time) : 'N/A',
        [t('reports.checkIn.table.status')]: ci.check_out_time ? t('reports.checkIn.status.checkedOut') : t('reports.checkIn.status.checkedIn'),
        [t('reports.checkIn.table.notes')]: ci.check_in_notes || '',
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      ws['!cols'] = [
        { wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 15 },
        { wch: 22 }, { wch: 22 }, { wch: 12 }, { wch: 14 }, { wch: 30 },
      ];

      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1976D2' } }, alignment: { horizontal: 'center', vertical: 'center' } };
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Quick Check-Ins');

      const checkedIn = checkInReportData.filter((ci: any) => !ci.check_out_time).length;
      const checkedOut = checkInReportData.filter((ci: any) => ci.check_out_time).length;
      const summaryData: any[] = [
        ['Report Summary'],
        ['Generated Date', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })],
        ['Total Check-Ins', checkInReportData.length],
        ['Currently Checked In', checkedIn],
        ['Checked Out', checkedOut],
      ];

      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      summaryWs['!cols'] = [{ wch: 25 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

      XLSX.writeFile(wb, `check-ins-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      notifySuccess(t('reports.messages.export.excel.success'));
    }).catch(() => {
      notifyError(t('reports.messages.export.excel.error'));
    });
  };

  if (!can('work-orders.view')) {
    return (
      <Box>
        <Alert severity="error">
          {t('reports.access.denied')}
        </Alert>
      </Box>
    );
  }

  const showCheckInsTab = can('quick-check-ins.view');
  const showOutOfSiteTab = can('out-of-site-tasks.reports');
  const showAttendanceTab = can('attendance.view');

  // Daily Activity (daily report) is shown first per requirements.
  let tabIndex = 0;
  const dailyActivityTabIndex = tabIndex++;
  const workOrdersTabIndex = tabIndex++;
  const expensesTabIndex = tabIndex++;
  const checkInsTabIndex = showCheckInsTab ? tabIndex++ : -1;
  const outOfSiteTabIndex = showOutOfSiteTab ? tabIndex++ : -1;
  const attendanceTabIndex = showAttendanceTab ? tabIndex++ : -1;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('reports.title')}</Typography>
      </Box>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab label={t('reports.tabs.dailyActivity')} icon={<TableChart />} />
        <Tab label={t('reports.tabs.workOrders')} icon={<TableChart />} />
        <Tab label={t('reports.tabs.expenses')} icon={<TableChart />} />
        {showCheckInsTab && (
          <Tab label={t('reports.tabs.checkIns')} icon={<TableChart />} />
        )}
        {showOutOfSiteTab && (
          <Tab label={t('reports.tabs.outOfSiteTasks')} icon={<TableChart />} />
        )}
        {showAttendanceTab && (
          <Tab label={t('reports.tabs.attendance')} icon={<AccessTime />} />
        )}
      </Tabs>

      {activeTab === workOrdersTabIndex && (
        <Box>
          {/* Filters Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FilterList color="primary" />
                <Typography variant="h6">
                  {t('reports.filters.title')}
                </Typography>
              </Box>
              
              {/* Primary filters — always visible */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.technician')}</InputLabel>
                  <Select
                    value={filters.technician_id}
                    label={t('reports.filters.technician')}
                    onChange={(e) => handleFilterChange('technician_id', e.target.value)}
                  >
                    <MenuItem value="">{t('reports.filters.technician.all')}</MenuItem>
                    {technicians.map((technician) => (
                      <MenuItem key={technician.id} value={technician.id.toString()}>
                        {(language === 'ar' && (technician.name_ar || technician.user?.name_ar) ? (technician.name_ar || technician.user?.name_ar) : (technician.name || technician.user?.name)) || technician.service_type} ({technician.service_type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.activityType')}</InputLabel>
                  <Select
                    multiple
                    value={filters.activity_types}
                    label={t('reports.filters.activityType')}
                    onChange={(e) => {
                      const val = e.target.value as string[];
                      if (val.includes('__all__')) {
                        const allTypes = ['check_in', 'check_out', 'pause', 'resume'];
                        handleFilterChange('activity_types', filters.activity_types.length === 4 ? [] : allTypes);
                      } else {
                        handleFilterChange('activity_types', val);
                      }
                    }}
                    renderValue={(selected) => {
                      const sel = selected as string[];
                      if (sel.length === 4) return t('reports.filters.activityType.allSelected');
                      return sel.map(v =>
                        v === 'check_in' ? t('reports.filters.activityType.checkIn')
                        : v === 'check_out' ? t('reports.filters.activityType.checkOut')
                        : v === 'pause' ? t('reports.filters.activityType.pause')
                        : t('reports.filters.activityType.resume')
                      ).join(', ');
                    }}
                  >
                    <MenuItem value="__all__">
                      <Checkbox checked={filters.activity_types.length === 4} indeterminate={filters.activity_types.length > 0 && filters.activity_types.length < 4} />
                      <ListItemText primary={filters.activity_types.length === 4 ? t('reports.filters.activityType.unselectAll') : t('reports.filters.activityType.selectAll')} />
                    </MenuItem>
                    {['check_in', 'check_out', 'pause', 'resume'].map(val => (
                      <MenuItem key={val} value={val}>
                        <Checkbox checked={filters.activity_types.includes(val)} />
                        <ListItemText primary={
                          val === 'check_in' ? t('reports.filters.activityType.checkIn')
                          : val === 'check_out' ? t('reports.filters.activityType.checkOut')
                          : val === 'pause' ? t('reports.filters.activityType.pause')
                          : t('reports.filters.activityType.resume')
                        } />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <DateField
                  fullWidth
                  label={t('reports.filters.dateFrom')}
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                />
              </Box>

              {/* More Filters toggle */}
              <Button
                size="small"
                onClick={() => setShowMoreFilters(!showMoreFilters)}
                endIcon={showMoreFilters ? <ExpandLess /> : <ExpandMore />}
                sx={{ mt: 2, textTransform: 'none' }}
              >
                {showMoreFilters ? t('reports.filters.lessFilters') : t('reports.filters.moreFilters')}
              </Button>

              {/* Secondary filters — hidden by default */}
              {showMoreFilters && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mt: 2 }}>
                  <DateField
                    fullWidth
                    label={t('reports.filters.dateTo')}
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  />

                  <FormControl fullWidth>
                    <InputLabel>{t('reports.filters.status')}</InputLabel>
                    <Select
                      multiple
                      value={filters.status}
                      label={t('reports.filters.status')}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                      <MenuItem value="pending">{t('reports.filters.status.pending')}</MenuItem>
                      <MenuItem value="in_progress">{t('reports.filters.status.inProgress')}</MenuItem>
                      <MenuItem value="completed">{t('reports.filters.status.completed')}</MenuItem>
                      <MenuItem value="cancelled">{t('reports.filters.status.cancelled')}</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t('reports.filters.createdBy')}</InputLabel>
                    <Select
                      value={filters.created_by}
                      label={t('reports.filters.createdBy')}
                      onChange={(e) => handleFilterChange('created_by', e.target.value)}
                    >
                      <MenuItem value="">{t('reports.filters.createdBy.all')}</MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id.toString()}>
                          {language === 'ar' && user.name_ar ? user.name_ar : user.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t('reports.filters.building')}</InputLabel>
                    <Select
                      value={filters.building_id}
                      label={t('reports.filters.building')}
                      onChange={(e) => handleFilterChange('building_id', e.target.value)}
                    >
                      <MenuItem value="">{t('reports.filters.building.all')}</MenuItem>
                      {buildings.map((building) => (
                        <MenuItem key={building.id} value={building.id.toString()}>
                          {language === 'ar' && building.name_ar ? building.name_ar : building.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t('reports.filters.flat')}</InputLabel>
                    <Select
                      value={filters.flat_id}
                      label={t('reports.filters.flat')}
                      onChange={(e) => handleFilterChange('flat_id', e.target.value)}
                      disabled={!filters.building_id}
                    >
                      <MenuItem value="">{t('reports.filters.flat.all')}</MenuItem>
                      {flats.map((flat) => (
                        <MenuItem key={flat.id} value={flat.id.toString()}>
                          {flat.flat_number}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t('reports.filters.priority')}</InputLabel>
                    <Select
                      multiple
                      value={filters.priority}
                      label={t('reports.filters.priority')}
                      onChange={(e) => handleFilterChange('priority', e.target.value)}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                      <MenuItem value="low">{t('reports.filters.priority.low')}</MenuItem>
                      <MenuItem value="medium">{t('reports.filters.priority.medium')}</MenuItem>
                      <MenuItem value="high">{t('reports.filters.priority.high')}</MenuItem>
                      <MenuItem value="urgent">{t('reports.filters.priority.urgent')}</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t('reports.filters.serviceType')}</InputLabel>
                    <Select
                      value={filters.service_type}
                      label={t('reports.filters.serviceType')}
                      onChange={(e) => handleFilterChange('service_type', e.target.value)}
                    >
                      <MenuItem value="">{t('reports.filters.serviceType.all')}</MenuItem>
                      {serviceTypes.map((st) => (
                        <MenuItem key={st} value={st}>
                          {st.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <DateField
                    fullWidth
                    label={t('reports.filters.scheduledFrom')}
                    value={filters.scheduled_from}
                    onChange={(e) => handleFilterChange('scheduled_from', e.target.value)}
                  />

                  <DateField
                    fullWidth
                    label={t('reports.filters.scheduledTo')}
                    value={filters.scheduled_to}
                    onChange={(e) => handleFilterChange('scheduled_to', e.target.value)}
                  />

                  <DateField
                    fullWidth
                    label={t('reports.filters.completedFrom')}
                    value={filters.completed_from}
                    onChange={(e) => handleFilterChange('completed_from', e.target.value)}
                  />

                  <DateField
                    fullWidth
                    label={t('reports.filters.completedTo')}
                    value={filters.completed_to}
                    onChange={(e) => handleFilterChange('completed_to', e.target.value)}
                  />
                </Box>
              )}

              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={generateReport}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <FilterList />}
                >
                  {t('reports.filters.generate')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFilters({
                      technician_id: '',
                      status: [],
                      priority: [],
                      service_type: '',
                      created_by: '',
                      building_id: '',
                      flat_id: '',
                      date_from: '',
                      date_to: '',
                      scheduled_from: '',
                      scheduled_to: '',
                      completed_from: '',
                      completed_to: '',
                      activity_types: [],
                    });
                    setReportData([]);
                  }}
                >
                  {t('reports.filters.clear')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Report Results */}
          {reportData.length > 0 && (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {t('reports.results.count').replace(
                      '{{count}}',
                      String(reportData.length)
                    )}
                  </Typography>
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdf />}
                      onClick={exportToPDF}
                    >
                      {t('reports.results.export.pdf')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<TableChart />}
                      onClick={exportToExcel}
                    >
                      {t('reports.results.export.excel')}
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 50px)', minHeight: 800 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={woSortField === 'work_order_number' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'work_order_number'} direction={woSortField === 'work_order_number' ? woSortDir : 'asc'} onClick={() => handleWoSort('work_order_number')}>
                            <strong>{t('reports.table.woNumber')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'title' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'title'} direction={woSortField === 'title' ? woSortDir : 'asc'} onClick={() => handleWoSort('title')}>
                            <strong>{t('reports.table.title')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <strong>{t('reports.table.description')}</strong>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'building' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'building'} direction={woSortField === 'building' ? woSortDir : 'asc'} onClick={() => handleWoSort('building')}>
                            <strong>{t('reports.table.building')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'flat' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'flat'} direction={woSortField === 'flat' ? woSortDir : 'asc'} onClick={() => handleWoSort('flat')}>
                            <strong>{t('reports.table.flat')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'provider' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'provider'} direction={woSortField === 'provider' ? woSortDir : 'asc'} onClick={() => handleWoSort('provider')}>
                            <strong>{t('reports.table.provider')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'status' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'status'} direction={woSortField === 'status' ? woSortDir : 'asc'} onClick={() => handleWoSort('status')}>
                            <strong>{t('reports.table.status')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'priority' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'priority'} direction={woSortField === 'priority' ? woSortDir : 'asc'} onClick={() => handleWoSort('priority')}>
                            <strong>{t('reports.table.priority')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'created_by' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'created_by'} direction={woSortField === 'created_by' ? woSortDir : 'asc'} onClick={() => handleWoSort('created_by')}>
                            <strong>{t('reports.table.createdBy')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'created_at' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'created_at'} direction={woSortField === 'created_at' ? woSortDir : 'asc'} onClick={() => handleWoSort('created_at')}>
                            <strong>{t('reports.table.createdDate')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'scheduled_from' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'scheduled_from'} direction={woSortField === 'scheduled_from' ? woSortDir : 'asc'} onClick={() => handleWoSort('scheduled_from')}>
                            <strong>{t('reports.table.scheduledFrom')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'scheduled_to' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'scheduled_to'} direction={woSortField === 'scheduled_to' ? woSortDir : 'asc'} onClick={() => handleWoSort('scheduled_to')}>
                            <strong>{t('reports.table.scheduledTo')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'completed_date' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'completed_date'} direction={woSortField === 'completed_date' ? woSortDir : 'asc'} onClick={() => handleWoSort('completed_date')}>
                            <strong>{t('reports.table.completedDate')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'check_in_time' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'check_in_time'} direction={woSortField === 'check_in_time' ? woSortDir : 'asc'} onClick={() => handleWoSort('check_in_time')}>
                            <strong>{t('reports.table.checkInTime')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'check_out_time' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'check_out_time'} direction={woSortField === 'check_out_time' ? woSortDir : 'asc'} onClick={() => handleWoSort('check_out_time')}>
                            <strong>{t('reports.table.checkOutTime')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={woSortField === 'wo_duration' ? woSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={woSortField === 'wo_duration'} direction={woSortField === 'wo_duration' ? woSortDir : 'asc'} onClick={() => handleWoSort('wo_duration')}>
                            <strong>{t('reports.table.duration')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <strong>{t('reports.table.pauseCount')}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{t('reports.table.lastPause')}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{t('reports.table.lastResume')}</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getSortedReportData().map((wo) => (
                        <TableRow key={wo.id} hover>
                          <TableCell>
                            <Box component="span" sx={linkSx} onClick={() => window.open(`/work-orders/${wo.id}`, '_blank')}>
                              {formatStringWithNumbers(wo.work_order_number, language)}
                            </Box>
                          </TableCell>
                          <TableCell>{language === 'ar' && wo.title_ar ? wo.title_ar : wo.title}</TableCell>
                          <TableCell>{wo.description || 'N/A'}</TableCell>
                          <TableCell>{language === 'ar' && wo.flat?.building?.name_ar ? wo.flat.building.name_ar : (wo.flat?.building?.name || 'N/A')}</TableCell>
                          <TableCell>{wo.flat?.flat_number ? formatStringWithNumbers(wo.flat.flat_number, language) : 'N/A'}</TableCell>
                          <TableCell>{(language === 'ar' && wo.technician?.user?.name_ar ? wo.technician.user.name_ar : wo.technician?.user?.name) || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={t(`workOrders.status.${wo.status === 'in_progress' ? 'inProgress' : wo.status === 'paused' ? 'paused' : wo.status}`)}
                              color={getStatusColor(wo.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={t(`workOrders.priority.${wo.priority}`)}
                              color={getPriorityColor(wo.priority) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{wo.created_by?.name || 'N/A'}</TableCell>
                          <TableCell>{formatDate(wo.created_at, language)}</TableCell>
                          <TableCell>
                            {wo.scheduled_from ? formatDate(wo.scheduled_from, language) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {wo.scheduled_to ? formatDate(wo.scheduled_to, language) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {wo.completed_date ? formatDate(wo.completed_date, language) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {wo.check_ins?.[0]?.check_in_time ? formatDateTime(wo.check_ins[0].check_in_time, language) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {wo.check_ins?.[0]?.check_out_time ? formatDateTime(wo.check_ins[0].check_out_time, language) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {wo.check_ins?.[0]?.check_in_time && wo.check_ins?.[0]?.check_out_time ? formatDuration(wo.check_ins[0].check_in_time, wo.check_ins[0].check_out_time) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {wo.pauses ? formatStringWithNumbers(String(wo.pauses.filter(p => p.action === 'pause').length), language) : '0'}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const ts = getLastPauseTime(wo);
                              return ts ? formatDateTime(ts, language) : 'N/A';
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const ts = getLastResumeTime(wo);
                              return ts ? formatDateTime(ts, language) : 'N/A';
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {reportData.length === 0 && !loading && (
            <Card>
              <CardContent>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 4 }}
                >
                  {t('reports.results.empty')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === expensesTabIndex && (
        <Box>
          {/* Expenses Filters Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FilterList color="primary" />
                <Typography variant="h6">
                  {t('reports.filters.title')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.workOrder')}</InputLabel>
                  <Select
                    value={expenseFilters.work_order_id}
                    label={t('reports.filters.workOrder')}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, work_order_id: e.target.value }))}
                  >
                    <MenuItem value="">{t('reports.filters.workOrder.all')}</MenuItem>
                    {workOrders.map((wo) => (
                      <MenuItem key={wo.id} value={wo.id.toString()}>
                        {wo.work_order_number} - {wo.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.serviceType')}</InputLabel>
                  <Select
                    value={expenseFilters.service_type}
                    label={t('reports.filters.serviceType')}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, service_type: e.target.value }))}
                  >
                    <MenuItem value="">{t('reports.filters.serviceType.all')}</MenuItem>
                    {expenseServiceTypes.map((st) => (
                      <MenuItem key={st} value={st}>
                        {st.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.building')}</InputLabel>
                  <Select
                    value={expenseFilters.building_id}
                    label={t('reports.filters.building')}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, building_id: e.target.value }))}
                  >
                    <MenuItem value="">{t('reports.filters.building.all')}</MenuItem>
                    {expenseBuildings.map((building) => (
                      <MenuItem key={building.id} value={building.id.toString()}>
                        {language === 'ar' && building.name_ar ? building.name_ar : building.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.flat')}</InputLabel>
                  <Select
                    value={expenseFilters.flat_id}
                    label={t('reports.filters.flat')}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, flat_id: e.target.value }))}
                    disabled={!expenseFilters.building_id}
                  >
                    <MenuItem value="">{t('reports.filters.flat.all')}</MenuItem>
                    {expenseFlats.map((flat) => (
                      <MenuItem key={flat.id} value={flat.id.toString()}>
                        {flat.flat_number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.vendor')}</InputLabel>
                  <Select
                    value={expenseFilters.vendor}
                    label={t('reports.filters.vendor')}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, vendor: e.target.value }))}
                  >
                    <MenuItem value="">{t('reports.filters.vendor.all')}</MenuItem>
                    {vendors.map((vendor) => (
                      <MenuItem key={vendor} value={vendor}>
                        {vendor}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <DateField
                  fullWidth
                  label={t('reports.filters.dateFrom')}
                  value={expenseFilters.date_from}
                  onChange={(e) => setExpenseFilters(prev => ({ ...prev, date_from: e.target.value }))}
                />

                <DateField
                  fullWidth
                  label={t('reports.filters.dateTo')}
                  value={expenseFilters.date_to}
                  onChange={(e) => setExpenseFilters(prev => ({ ...prev, date_to: e.target.value }))}
                />

                <TextField
                  fullWidth
                  label={t('reports.filters.amountMin')}
                  type="number"
                  value={expenseFilters.amount_min}
                  onChange={(e) => setExpenseFilters(prev => ({ ...prev, amount_min: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  autoComplete="off"
                />

                <TextField
                  fullWidth
                  label={t('reports.filters.amountMax')}
                  type="number"
                  value={expenseFilters.amount_max}
                  onChange={(e) => setExpenseFilters(prev => ({ ...prev, amount_max: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  autoComplete="off"
                />
              </Box>

              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={generateExpenseReport}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <FilterList />}
                >
                  {t('reports.filters.generate')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setExpenseFilters({
                      work_order_id: '',
                      service_type: '',
                      building_id: '',
                      flat_id: '',
                      vendor: '',
                      date_from: '',
                      date_to: '',
                      amount_min: '',
                      amount_max: '',
                    });
                    setExpenseReportData([]);
                  }}
                >
                  {t('reports.filters.clear')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Expense Report Results */}
          {expenseReportData.length > 0 && (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {t('reports.results.count').replace('{{count}}', String(expenseReportData.length))}
                  </Typography>
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdf />}
                      onClick={exportExpensesToPDF}
                    >
                      {t('reports.results.export.pdf')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<TableChart />}
                      onClick={exportExpensesToExcel}
                    >
                      {t('reports.results.export.excel')}
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 250px)', minHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={expSortField === 'expense_number' ? expSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={expSortField === 'expense_number'} direction={expSortField === 'expense_number' ? expSortDir : 'asc'} onClick={() => handleExpSort('expense_number')}>
                            <strong>{t('reports.table.expenseNumber')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={expSortField === 'title' ? expSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={expSortField === 'title'} direction={expSortField === 'title' ? expSortDir : 'asc'} onClick={() => handleExpSort('title')}>
                            <strong>{t('reports.table.title')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell><strong>{t('reports.table.description')}</strong></TableCell>
                        <TableCell sortDirection={expSortField === 'work_order_number' ? expSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={expSortField === 'work_order_number'} direction={expSortField === 'work_order_number' ? expSortDir : 'asc'} onClick={() => handleExpSort('work_order_number')}>
                            <strong>{t('reports.table.workOrder')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={expSortField === 'service_type' ? expSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={expSortField === 'service_type'} direction={expSortField === 'service_type' ? expSortDir : 'asc'} onClick={() => handleExpSort('service_type')}>
                            <strong>{t('reports.table.serviceType')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={expSortField === 'building' ? expSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={expSortField === 'building'} direction={expSortField === 'building' ? expSortDir : 'asc'} onClick={() => handleExpSort('building')}>
                            <strong>{t('reports.table.building')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell><strong>{t('reports.table.flat')}</strong></TableCell>
                        <TableCell sortDirection={expSortField === 'vendor' ? expSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={expSortField === 'vendor'} direction={expSortField === 'vendor' ? expSortDir : 'asc'} onClick={() => handleExpSort('vendor')}>
                            <strong>{t('reports.table.vendor')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={expSortField === 'amount' ? expSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={expSortField === 'amount'} direction={expSortField === 'amount' ? expSortDir : 'asc'} onClick={() => handleExpSort('amount')}>
                            <strong>{t('reports.table.amount')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={expSortField === 'expense_date' ? expSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={expSortField === 'expense_date'} direction={expSortField === 'expense_date' ? expSortDir : 'asc'} onClick={() => handleExpSort('expense_date')}>
                            <strong>{t('reports.table.expenseDate')}</strong>
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getSortedExpenseData().map((expense) => (
                        <TableRow key={expense.id} hover>
                          <TableCell>
                            {expense.work_order?.id ? (
                              <Box component="span" sx={linkSx} onClick={() => window.open(`/work-orders/${expense.work_order.id}#expenses`, '_blank')}>
                                {formatStringWithNumbers(expense.expense_number, language)}
                              </Box>
                            ) : (
                              formatStringWithNumbers(expense.expense_number, language)
                            )}
                          </TableCell>
                          <TableCell>{expense.title || 'N/A'}</TableCell>
                          <TableCell>{expense.notes || 'N/A'}</TableCell>
                          <TableCell>
                            {expense.work_order?.work_order_number ? (
                              <Box component="span" sx={linkSx} onClick={() => window.open(`/work-orders/${expense.work_order.id}`, '_blank')}>
                                {formatStringWithNumbers(expense.work_order.work_order_number, language)}
                              </Box>
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell>{(() => {
                            const serviceType = expense.service_type || expense.work_order?.technician?.service_type || '';
                            const serviceTypeObj = serviceTypesObjects.find(st => st.name_en === serviceType);
                            return serviceTypeObj ? (language === 'ar' ? serviceTypeObj.name_ar : serviceTypeObj.name_en).toUpperCase() : (serviceType || 'N/A').toUpperCase();
                          })()}</TableCell>
                          <TableCell>{language === 'ar' && (expense.building?.name_ar || expense.work_order?.flat?.building?.name_ar) ? (expense.building?.name_ar || expense.work_order?.flat?.building?.name_ar) : (expense.building?.name || expense.work_order?.flat?.building?.name || 'N/A')}</TableCell>
                          <TableCell>{expense.flat?.flat_number ? formatStringWithNumbers(expense.flat.flat_number, language) : (expense.work_order?.flat?.flat_number ? formatStringWithNumbers(expense.work_order.flat.flat_number, language) : 'N/A')}</TableCell>
                          <TableCell>{expense.vendor || 'N/A'}</TableCell>
                          <TableCell>{formatCurrency(expense.amount || 0, language)}</TableCell>
                          <TableCell>{expense.expense_date ? formatDate(expense.expense_date, language) : 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {expenseReportData.length === 0 && !loading && (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {t('reports.results.empty')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === checkInsTabIndex && showCheckInsTab && (
        <Box>
          {/* Check-In Filters Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FilterList color="primary" />
                <Typography variant="h6">{t('reports.filters.title')}</Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('reports.checkIn.filters.technician')}</InputLabel>
                  <Select
                    value={checkInFilters.user_id}
                    label={t('reports.checkIn.filters.technician')}
                    onChange={(e) => setCheckInFilters(prev => ({ ...prev, user_id: e.target.value }))}
                  >
                    <MenuItem value="">{t('reports.filters.technician.all')}</MenuItem>
                    {checkInTechnicians.map((tech) => (
                      <MenuItem key={tech.id} value={tech.id.toString()}>{tech.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.building')}</InputLabel>
                  <Select
                    value={checkInFilters.building_id}
                    label={t('reports.filters.building')}
                    onChange={(e) => setCheckInFilters(prev => ({ ...prev, building_id: e.target.value }))}
                  >
                    <MenuItem value="">{t('reports.filters.building.all')}</MenuItem>
                    {checkInBuildings.map((building) => (
                      <MenuItem key={building.id} value={building.id.toString()}>
                        {language === 'ar' && building.name_ar ? building.name_ar : building.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('reports.checkIn.filters.status')}</InputLabel>
                  <Select
                    value={checkInFilters.status}
                    label={t('reports.checkIn.filters.status')}
                    onChange={(e) => setCheckInFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <MenuItem value="">{t('reports.checkIn.filters.statusAll')}</MenuItem>
                    <MenuItem value="checked_in">{t('reports.checkIn.status.checkedIn')}</MenuItem>
                    <MenuItem value="checked_out">{t('reports.checkIn.status.checkedOut')}</MenuItem>
                  </Select>
                </FormControl>

                <DateField
                  fullWidth
                  label={t('reports.filters.dateFrom')}
                  value={checkInFilters.date_from}
                  onChange={(e) => setCheckInFilters(prev => ({ ...prev, date_from: e.target.value }))}
                />

                <DateField
                  fullWidth
                  label={t('reports.filters.dateTo')}
                  value={checkInFilters.date_to}
                  onChange={(e) => setCheckInFilters(prev => ({ ...prev, date_to: e.target.value }))}
                />
              </Box>

              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={generateCheckInReport}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <FilterList />}
                >
                  {t('reports.filters.generate')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCheckInFilters({ user_id: '', building_id: '', status: '', date_from: '', date_to: '' });
                    setCheckInReportData([]);
                  }}
                >
                  {t('reports.filters.clear')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Check-In Report Results */}
          {checkInReportData.length > 0 && (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {t('reports.results.count').replace('{{count}}', String(checkInReportData.length))}
                  </Typography>
                  <Box display="flex" gap={2}>
                    <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={exportCheckInsToPDF}>
                      {t('reports.results.export.pdf')}
                    </Button>
                    <Button variant="outlined" startIcon={<TableChart />} onClick={exportCheckInsToExcel}>
                      {t('reports.results.export.excel')}
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 250px)', minHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={ciSortField === 'technician' ? ciSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={ciSortField === 'technician'} direction={ciSortField === 'technician' ? ciSortDir : 'asc'} onClick={() => handleCiSort('technician')}>
                            <strong>{t('reports.checkIn.table.technician')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={ciSortField === 'building' ? ciSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={ciSortField === 'building'} direction={ciSortField === 'building' ? ciSortDir : 'asc'} onClick={() => handleCiSort('building')}>
                            <strong>{t('reports.checkIn.table.building')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell><strong>{t('reports.checkIn.table.amenities')}</strong></TableCell>
                        <TableCell><strong>{t('reports.checkIn.table.flats')}</strong></TableCell>
                        <TableCell sortDirection={ciSortField === 'check_in_time' ? ciSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={ciSortField === 'check_in_time'} direction={ciSortField === 'check_in_time' ? ciSortDir : 'asc'} onClick={() => handleCiSort('check_in_time')}>
                            <strong>{t('reports.checkIn.table.checkInTime')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={ciSortField === 'check_out_time' ? ciSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={ciSortField === 'check_out_time'} direction={ciSortField === 'check_out_time' ? ciSortDir : 'asc'} onClick={() => handleCiSort('check_out_time')}>
                            <strong>{t('reports.checkIn.table.checkOutTime')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={ciSortField === 'duration' ? ciSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={ciSortField === 'duration'} direction={ciSortField === 'duration' ? ciSortDir : 'asc'} onClick={() => handleCiSort('duration')}>
                            <strong>{t('reports.checkIn.table.duration')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={ciSortField === 'status' ? ciSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={ciSortField === 'status'} direction={ciSortField === 'status' ? ciSortDir : 'asc'} onClick={() => handleCiSort('status')}>
                            <strong>{t('reports.checkIn.table.status')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell><strong>{t('reports.checkIn.table.notes')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getSortedCheckInData().map((ci: any) => (
                        <TableRow key={ci.id} hover>
                          <TableCell>{(language === 'ar' && ci.user?.name_ar ? ci.user.name_ar : ci.user?.name) || 'N/A'}</TableCell>
                          <TableCell>{language === 'ar' && ci.building?.name_ar ? ci.building.name_ar : (ci.building?.name || 'N/A')}</TableCell>
                          <TableCell>{ci.amenities?.map((a: any) => language === 'ar' ? a.name_ar : a.name_en).join(', ') || 'N/A'}</TableCell>
                          <TableCell>{ci.selected_flats?.map((f: any) => f.flat_number).join(', ') || 'N/A'}</TableCell>
                          <TableCell>{ci.check_in_time ? formatDateTime(ci.check_in_time, language) : 'N/A'}</TableCell>
                          <TableCell>{ci.check_out_time ? formatDateTime(ci.check_out_time, language) : 'N/A'}</TableCell>
                          <TableCell>{ci.check_in_time && ci.check_out_time ? formatDuration(ci.check_in_time, ci.check_out_time) : 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={ci.check_out_time ? t('reports.checkIn.status.checkedOut') : t('reports.checkIn.status.checkedIn')}
                              color={ci.check_out_time ? 'default' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{ci.check_in_notes || ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {checkInReportData.length === 0 && !loading && (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {t('reports.results.empty')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === outOfSiteTabIndex && showOutOfSiteTab && (
        <Box>
          {/* Out-of-Site Tasks Filters Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FilterList color="primary" />
                <Typography variant="h6">
                  {t('reports.filters.title')}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                  gap: 2,
                }}
              >
                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.technician')}</InputLabel>
                  <Select
                    value={outOfSiteFilters.technician_id}
                    label={t('reports.filters.technician')}
                    onChange={(e) => setOutOfSiteFilters(prev => ({ ...prev, technician_id: e.target.value }))}
                  >
                    <MenuItem value="">{t('reports.filters.technician.all')}</MenuItem>
                    {technicians.map((technician) => (
                      <MenuItem key={technician.id} value={technician.id.toString()}>
                        {(language === 'ar' && (technician.name_ar || technician.user?.name_ar)
                          ? (technician.name_ar || technician.user?.name_ar)
                          : (technician.name || technician.user?.name)) || technician.service_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={outOfSiteFilters.status}
                    label="Status"
                    onChange={(e) => setOutOfSiteFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>

                <DateField
                  fullWidth
                  label={t('reports.filters.dateFrom')}
                  value={outOfSiteFilters.date_from}
                  onChange={(e) => setOutOfSiteFilters(prev => ({ ...prev, date_from: e.target.value }))}
                />

                <DateField
                  fullWidth
                  label={t('reports.filters.dateTo')}
                  value={outOfSiteFilters.date_to}
                  onChange={(e) => setOutOfSiteFilters(prev => ({ ...prev, date_to: e.target.value }))}
                />
              </Box>

              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={() => fetchOutOfSiteTasks(1)}
                  disabled={outOfSiteLoading}
                  startIcon={outOfSiteLoading ? <CircularProgress size={20} /> : <FilterList />}
                >
                  {t('reports.filters.generate')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setOutOfSiteFilters({
                      technician_id: '',
                      status: 'all',
                      date_from: '',
                      date_to: '',
                    });
                    setOutOfSiteTasks([]);
                    setOutOfSitePage(1);
                    setOutOfSiteTotalPages(1);
                    setOutOfSiteTotalCount(0);
                  }}
                >
                  {t('reports.filters.clear')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Out-of-Site Tasks Report Results */}
          {outOfSiteTasks.length > 0 && (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {t('reports.results.count').replace(
                      '{{count}}',
                      String(outOfSiteTotalCount || outOfSiteTasks.length),
                    )}
                  </Typography>
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdf />}
                      onClick={exportOutOfSiteToPDF}
                    >
                      {t('reports.results.export.pdf')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<TableChart />}
                      onClick={exportOutOfSiteToExcel}
                    >
                      {t('reports.results.export.excel')}
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 250px)', minHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('reports.table.provider')}</strong></TableCell>
                        <TableCell><strong>{t('reports.outOfSite.table.objective')}</strong></TableCell>
                        <TableCell><strong>{t('reports.outOfSite.table.startTime')}</strong></TableCell>
                        <TableCell><strong>{t('reports.outOfSite.table.expectedEnd')}</strong></TableCell>
                        <TableCell><strong>{t('reports.outOfSite.table.endTime')}</strong></TableCell>
                        <TableCell><strong>{t('reports.outOfSite.table.status')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {outOfSiteTasks.map((task) => (
                        <TableRow key={task.id} hover>
                          <TableCell>
                            {task.technician?.user
                              ? (language === 'ar' && task.technician.user.name_ar
                                ? task.technician.user.name_ar
                                : task.technician.user.name)
                              : 'N/A'}
                          </TableCell>
                          <TableCell>{task.objective}</TableCell>
                          <TableCell>
                            {task.start_time ? formatDateTime(task.start_time, language) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {task.expected_end_time ? formatDateTime(task.expected_end_time, language) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {task.end_time ? formatDateTime(task.end_time, language) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={task.status.replace('_', ' ')}
                              color={getStatusColor(task.status) as any}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Total: {outOfSiteTotalCount} task{outOfSiteTotalCount === 1 ? '' : 's'}
                  </Typography>
                  {outOfSiteTotalPages > 1 && (
                    <Pagination
                      count={outOfSiteTotalPages}
                      page={outOfSitePage}
                      onChange={(_event: React.ChangeEvent<unknown>, page: number) => fetchOutOfSiteTasks(page)}
                      color="primary"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {outOfSiteTasks.length === 0 && !outOfSiteLoading && (
            <Card>
              <CardContent>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 4 }}
                >
                  {t('reports.results.empty')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
      {/* Daily Activity Tab */}
      {activeTab === dailyActivityTabIndex && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FilterList color="primary" />
                <Typography variant="h6">{t('reports.dailyActivity.filters.title')}</Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.technician')}</InputLabel>
                  <Select
                    value={dailyActivityTechId}
                    label={t('reports.filters.technician')}
                    onChange={(e) => setDailyActivityTechId(e.target.value)}
                  >
                    <MenuItem value="">{t('reports.filters.technician.all')}</MenuItem>
                    {technicians.map((technician) => (
                      <MenuItem key={technician.id} value={technician.id.toString()}>
                        {(language === 'ar' && (technician.name_ar || technician.user?.name_ar) ? (technician.name_ar || technician.user?.name_ar) : (technician.name || technician.user?.name)) || technician.service_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="date"
                  label={t('reports.dailyActivity.filters.date')}
                  value={dailyActivityDate}
                  onChange={(e) => setDailyActivityDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                <Box display="flex" gap={1} alignItems="center">
                  <Button
                    variant="contained"
                    onClick={generateDailyActivity}
                    disabled={dailyActivityLoading}
                    startIcon={dailyActivityLoading ? <CircularProgress size={20} /> : <FilterList />}
                  >
                    {t('reports.filters.generate')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setDailyActivityDate(new Date().toISOString().slice(0, 10));
                      setDailyActivityTechId('');
                      setDailyActivityData([]);
                    }}
                  >
                    {t('reports.filters.clear')}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {dailyActivityData.length > 0 && (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {t('reports.dailyActivity.results.title').replace('{{count}}', String(dailyActivityData.length)).replace('{{date}}', dailyActivityDate)}
                  </Typography>
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 250px)', minHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={daSortField === 'time' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'time'} direction={daSortField === 'time' ? daSortDir : 'asc'} onClick={() => handleDaSort('time')}>
                            <strong>{t('reports.dailyActivity.table.time')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daSortField === 'type' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'type'} direction={daSortField === 'type' ? daSortDir : 'asc'} onClick={() => handleDaSort('type')}>
                            <strong>{t('reports.dailyActivity.table.type')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daSortField === 'technician' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'technician'} direction={daSortField === 'technician' ? daSortDir : 'asc'} onClick={() => handleDaSort('technician')}>
                            <strong>{t('reports.dailyActivity.table.technician')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daSortField === 'reference' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'reference'} direction={daSortField === 'reference' ? daSortDir : 'asc'} onClick={() => handleDaSort('reference')}>
                            <strong>{t('reports.dailyActivity.table.reference')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daSortField === 'title' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'title'} direction={daSortField === 'title' ? daSortDir : 'asc'} onClick={() => handleDaSort('title')}>
                            <strong>{t('reports.dailyActivity.table.title')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daSortField === 'building' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'building'} direction={daSortField === 'building' ? daSortDir : 'asc'} onClick={() => handleDaSort('building')}>
                            <strong>{t('reports.dailyActivity.table.building')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daSortField === 'status' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'status'} direction={daSortField === 'status' ? daSortDir : 'asc'} onClick={() => handleDaSort('status')}>
                            <strong>{t('reports.dailyActivity.table.status')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <strong>{t('reports.dailyActivity.table.actions')}</strong>
                        </TableCell>
                        <TableCell sortDirection={daSortField === 'start_time' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'start_time'} direction={daSortField === 'start_time' ? daSortDir : 'asc'} onClick={() => handleDaSort('start_time')}>
                            <strong>{t('reports.dailyActivity.table.startTime')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daSortField === 'end_time' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'end_time'} direction={daSortField === 'end_time' ? daSortDir : 'asc'} onClick={() => handleDaSort('end_time')}>
                            <strong>{t('reports.dailyActivity.table.endTime')}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daSortField === 'duration' ? daSortDir : false}>
                          <TableSortLabel sx={sortLabelSx} active={daSortField === 'duration'} direction={daSortField === 'duration' ? daSortDir : 'asc'} onClick={() => handleDaSort('duration')}>
                            <strong>{t('reports.dailyActivity.table.duration')}</strong>
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getSortedDailyActivity().map((row: any, idx: number) => {
                        const typeLabel = row.type === 'work_order' ? t('reports.dailyActivity.type.workOrder')
                          : row.type === 'quick_check_in' ? t('reports.dailyActivity.type.quickCheckIn')
                          : row.type === 'out_of_site' ? t('reports.dailyActivity.type.outOfSite')
                          : row.type;
                        const typeColor = row.type === 'work_order' ? 'primary'
                          : row.type === 'quick_check_in' ? 'secondary'
                          : row.type === 'out_of_site' ? 'info'
                          : 'default';
                        const durationStr = row.duration_minutes != null
                          ? `${Math.floor(Math.round(row.duration_minutes) / 60)}h ${Math.round(row.duration_minutes) % 60}m`
                          : '-';
                        return (
                          <TableRow key={idx} hover>
                            <TableCell>{row.start_time ? new Date(row.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                            <TableCell><Chip label={typeLabel} color={typeColor as any} size="small" /></TableCell>
                            <TableCell>{row.technician}</TableCell>
                            <TableCell
                              sx={(row.work_order_id || row.check_in_id || row.out_of_site_id) ? { color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' } : {}}
                              onClick={() => {
                                if (row.work_order_id) window.open(`/work-orders/${row.work_order_id}`, '_blank');
                                else if (row.check_in_id) window.open(`/quick-check-ins/${row.check_in_id}`, '_blank');
                                else if (row.out_of_site_id) window.open(`/out-of-site-tasks/${row.out_of_site_id}`, '_blank');
                              }}
                            >
                              {row.reference}
                            </TableCell>
                            <TableCell>{row.title}</TableCell>
                            <TableCell>{row.building}</TableCell>
                            <TableCell>
                              <Chip label={row.status?.replace('_', ' ')} size="small" />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {row.actions && row.actions.length > 0 ? row.actions.map((a: any, i: number) => {
                                  const actionLabel = a.action === 'check_in' ? t('reports.dailyActivity.action.checkIn')
                                    : a.action === 'check_out' ? t('reports.dailyActivity.action.checkOut')
                                    : a.action === 'pause' ? t('reports.dailyActivity.action.pause')
                                    : a.action === 'resume' ? t('reports.dailyActivity.action.resume')
                                    : a.action;
                                  const actionColor = a.action === 'check_in' ? 'success'
                                    : a.action === 'check_out' ? 'primary'
                                    : a.action === 'pause' ? 'warning'
                                    : a.action === 'resume' ? 'info'
                                    : 'default';
                                  const timeStr = a.time ? new Date(a.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                                  return <Chip key={i} label={`${actionLabel} ${timeStr}`} color={actionColor as any} size="small" variant="outlined" />;
                                }) : '-'}
                              </Box>
                            </TableCell>
                            <TableCell>{row.start_time ? formatDateTime(row.start_time, language) : '-'}</TableCell>
                            <TableCell>{row.end_time ? formatDateTime(row.end_time, language) : '-'}</TableCell>
                            <TableCell>{durationStr}</TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Footer row with total duration */}
                      {(() => {
                        const totalMins = dailyActivityData.reduce((sum: number, r: any) => sum + (r.duration_minutes || 0), 0);
                        return (
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell colSpan={10} align="right"><strong>{t('reports.dailyActivity.table.totalDuration')}</strong></TableCell>
                            <TableCell><strong>{`${Math.floor(Math.round(totalMins) / 60)}h ${Math.round(totalMins) % 60}m`}</strong></TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {dailyActivityData.length === 0 && !dailyActivityLoading && (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {t('reports.dailyActivity.empty')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {showAttendanceTab && activeTab === attendanceTabIndex && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FilterList color="primary" />
                <Typography variant="h6">{t('reports.attendance.filters.title')}</Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('reports.filters.technician')}</InputLabel>
                  <Select
                    value={attendanceTechId}
                    label={t('reports.filters.technician')}
                    onChange={(e) => setAttendanceTechId(e.target.value)}
                  >
                    <MenuItem value="">{t('reports.filters.technician.all')}</MenuItem>
                    {technicians.map((technician) => (
                      <MenuItem key={technician.id} value={technician.id.toString()}>
                        {(language === 'ar' && (technician.name_ar || technician.user?.name_ar) ? (technician.name_ar || technician.user?.name_ar) : (technician.name || technician.user?.name)) || technician.service_type} ({technician.service_type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('reports.attendance.filters.type')}</InputLabel>
                  <Select
                    value={attendanceServiceType}
                    label={t('reports.attendance.filters.type')}
                    onChange={(e) => setAttendanceServiceType(e.target.value)}
                  >
                    <MenuItem value="">{t('reports.attendance.filters.type.all')}</MenuItem>
                    {serviceTypes.map((st) => (
                      <MenuItem key={st} value={st}>{st}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('reports.attendance.filters.period')}</InputLabel>
                  <Select
                    value={attendancePeriod}
                    label={t('reports.attendance.filters.period')}
                    onChange={(e) => setAttendancePeriod(e.target.value as any)}
                  >
                    <MenuItem value="daily">{t('reports.attendance.period.daily')}</MenuItem>
                    <MenuItem value="weekly">{t('reports.attendance.period.weekly')}</MenuItem>
                    <MenuItem value="monthly">{t('reports.attendance.period.monthly')}</MenuItem>
                    <MenuItem value="custom">{t('reports.attendance.period.custom')}</MenuItem>
                  </Select>
                </FormControl>

                {attendancePeriod === 'custom' ? (
                  <DateField
                    fullWidth
                    label={t('reports.filters.dateFrom')}
                    value={attendanceDateFrom}
                    onChange={(e) => setAttendanceDateFrom(e.target.value)}
                  />
                ) : (
                  <TextField
                    fullWidth
                    type="date"
                    label={attendancePeriod === 'daily' ? t('reports.dailyActivity.filters.date') : t('reports.attendance.filters.refDate')}
                    value={attendanceDate}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}

                {attendancePeriod === 'custom' && (
                  <DateField
                    fullWidth
                    label={t('reports.filters.dateTo')}
                    value={attendanceDateTo}
                    onChange={(e) => setAttendanceDateTo(e.target.value)}
                  />
                )}

                <Box display="flex" gap={1} alignItems="center">
                  <Button
                    variant="contained"
                    onClick={generateAttendanceReport}
                    disabled={attendanceLoading}
                    startIcon={attendanceLoading ? <CircularProgress size={20} /> : <FilterList />}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {t('reports.filters.generate')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setAttendancePeriod('daily');
                      setAttendanceDate(new Date().toISOString().slice(0, 10));
                      setAttendanceDateFrom('');
                      setAttendanceDateTo('');
                      setAttendanceTechId('');
                      setAttendanceServiceType('');
                      setAttendanceData([]);
                    }}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {t('reports.filters.clear')}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {attendanceData.length > 0 && (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                  <Typography variant="h6">
                    {t('reports.attendance.results.title').replace('{{count}}', String(attendanceData.length))}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={exportAttendanceToPDF} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{t('reports.results.export.pdf')}</Button>
                    <Button variant="outlined" startIcon={<TableChart />} onClick={exportAttendanceToExcel} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{t('reports.results.export.excel')}</Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 250px)', minHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('reports.attendance.table.date')}</strong></TableCell>
                        <TableCell><strong>{t('reports.attendance.table.technician')}</strong></TableCell>
                        <TableCell><strong>{t('reports.attendance.table.firstCheckInTime')}</strong></TableCell>
                        <TableCell><strong>{t('reports.attendance.table.firstCheckInLocation')}</strong></TableCell>
                        <TableCell><strong>{t('reports.attendance.table.lastCheckOutTime')}</strong></TableCell>
                        <TableCell><strong>{t('reports.attendance.table.lastCheckOutLocation')}</strong></TableCell>
                        <TableCell><strong>{t('reports.attendance.table.totalHours')}</strong></TableCell>
                        <TableCell><strong>{t('reports.attendance.table.status')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attendanceData.map((r: any) => (
                        <TableRow key={r.id} hover>
                          <TableCell>{formatDate(r.date, language)}</TableCell>
                          <TableCell>{attTechName(r)}</TableCell>
                          <TableCell>{r.clock_in_time ? formatDateTime(r.clock_in_time, language) : '-'}</TableCell>
                          <TableCell>
                            <LocationViewer lat={r.clock_in_lat} lng={r.clock_in_lng} title={t('reports.attendance.table.firstCheckIn')} />
                          </TableCell>
                          <TableCell>{r.clock_out_time ? formatDateTime(r.clock_out_time, language) : '-'}</TableCell>
                          <TableCell>
                            <LocationViewer lat={r.clock_out_lat} lng={r.clock_out_lng} title={t('reports.attendance.table.lastCheckOut')} />
                          </TableCell>
                          <TableCell>{attDuration(r.working_hours_minutes)}</TableCell>
                          <TableCell>
                            <Chip label={t(`attendance.status.${r.status}`)} size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                      {(() => {
                        const totalMins = attendanceData.reduce((s: number, r: any) => s + (r.working_hours_minutes || 0), 0);
                        return (
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell colSpan={6} align="right"><strong>{t('reports.attendance.table.totalWorkingHours')}</strong></TableCell>
                            <TableCell colSpan={2}><strong>{attDuration(totalMins)}</strong></TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {attendanceData.length === 0 && !attendanceLoading && (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {t('reports.attendance.empty')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Reports;

